import {
  Badge,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  UnorderedList,
  useToast,
  VStack
} from "@chakra-ui/react";
import '../common.css';
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import ReadOnlyExemptButton from "../../components/ReadOnlyExemptButton";
import { useTranslation } from "react-i18next";
import { FaRegQuestionCircle } from "react-icons/fa";
import { codeToNameMap, ShiftSystem } from "../../contexts/constants";
import {
  Currency,
  CycleTimeInSecondByPartNoType,
  EquipmentType,
  Errors,
  PagePermissions,
  RationalizationProductionCapacityData,
  RationalizationProductionCapacityDataByPartNo,
  UniqueProcessType,
  RationalizationSummaryInputData,
  RationalizationProfitLossItem,
  RationalizationProfitLossItemType,
} from "../../contexts/types";
import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";
import { FiTrash2, FiChevronUp, FiChevronDown } from "react-icons/fi";
import { formatNumber, hasDecimal, hourMinutesStringToMinutes, resolveDisplayPartNoByKey } from "../../utils/utils";
import { calcEquipmentDepreciationByYearMonth, convertEquipmentData } from "../../utils/calculation";
import { WebAPIContext } from "../../contexts/WebAPIContext";
import CeasTimePicker from "../InvestmentScreening/CeasTimePicker";
import GoogleAPI from "../../utils/GoogleAPI";

/**
 * 指標グループの項目選択肢（value/unit のみ保持。label は useMemo で t() 付与）
 */
const INDICATOR_ITEM_OPTIONS_BASE: { value: RationalizationProfitLossItemType | ""; unit: "pcs" | "currency" }[] = [
  { value: "", unit: "pcs" },
  { value: "productionQuantity", unit: "pcs" },
  { value: "salesQuantity", unit: "pcs" },
  { value: "custom", unit: "currency" },
];

/**
 * 費用グループの項目選択肢（value/unit のみ保持。label は useMemo で t() 付与）
 */
const COST_ITEM_OPTIONS_BASE: { value: RationalizationProfitLossItemType | ""; unit: "pcs" | "currency" }[] = [
  { value: "", unit: "pcs" },
  { value: "laborCost", unit: "currency" },
  { value: "repairCost", unit: "currency" },
  { value: "consumablesCost", unit: "currency" },
  { value: "maintenanceCost", unit: "currency" },
  { value: "scrapCost", unit: "currency" },
  { value: "targetEquipmentDepreciation", unit: "currency" },
  { value: "otherDepreciation", unit: "currency" },
  { value: "custom", unit: "currency" },
];

/** 削除不可の必須指標項目タイプ（簡易モード時のみ追加で必須化される項目） */
const REQUIRED_INDICATOR_ITEM_TYPES_SIMPLE_ONLY: RationalizationProfitLossItemType[] = [
  "salesQuantity",
];

/** 削除不可の必須費用項目タイプ（詳細モード・簡易モード共通） */
const REQUIRED_COST_ITEM_TYPES_COMMON: RationalizationProfitLossItemType[] = [
  "targetEquipmentDepreciation",
  "otherDepreciation",
];

/** 削除不可の必須費用項目タイプ（簡易モード時のみ追加で必須化される項目） */
const REQUIRED_COST_ITEM_TYPES_SIMPLE_ONLY: RationalizationProfitLossItemType[] = [
  "laborCost",
];

/** 項目タイプから単位種別を判定 */
const getDefaultUnitTypeByItemType = (itemType: RationalizationProfitLossItemType): "pcs" | "currency" => {
  if (itemType === "productionQuantity" || itemType === "salesQuantity") return "pcs";
  return "currency";
};

/**
 * 型定義（label付き）
 */
type ItemOption = { value: RationalizationProfitLossItemType | ""; label: string; unit: "pcs" | "currency" };

/**
 * 合理化前後サマリーPopoverのプロパティ
 */
interface Props {
  /** プロジェクトID */
  projectId: string;
  /** プロセスID（工程ID） */
  processId?: string;
  /** 工程名 */
  itemNo?: string;
  /** 既存の合理化前後サマリーデータ */
  rationalizationSummaryData?: RationalizationSummaryInputData | null;
  /** プロセスコードデータ */
  processCodeData: UniqueProcessType[];
  /** 保存ボタン活性制御 */
  isSave: boolean;
  /** 保存ハンドラー */
  onSave: (data: RationalizationSummaryInputData) => Promise<boolean>;
  /** 画面パーミッション */
  pagePermissions?: PagePermissions;
  /** ライン情報 */
  productionLineData?: any;
  /** 量産開始日（YYYY-MM-DD形式） */
  projectStartDate: string | null;
  /** EOL予定日（YYYY-MM-DD形式） */
  projectEndDate: string | null;
  /** ローカル通貨コード（例: JPY, USD） */
  localCurrency?: string | null;
  /** 通貨データ */
  currencyData?: Currency[];
  /** 簡易モードか（true時のみ新規必須項目を自動追加） */
  isSimpleMode?: boolean;
  /** PartNoデータ（displayPartNo解決用、No.435対応） */
  partNoData?: { partNo: string; displayPartNo?: string }[];
}

/**
 * 合理化前後サマリーPopover
 */
const RationalizationSummaryInputPopover: React.FC<Props> = ({
  projectId,
  processId,
  itemNo,
  rationalizationSummaryData,
  processCodeData,
  isSave,
  onSave,
  pagePermissions,
  productionLineData,
  projectStartDate: massProductionStartDate,
  projectEndDate: eolDate,
  localCurrency = "JPY",
  currencyData = [],
  isSimpleMode = false,
  partNoData,
}) => {

  const { t } = useTranslation();

  /** 現在のモードで必須となる費用項目タイプ */
  const requiredCostItemTypes: RationalizationProfitLossItemType[] = isSimpleMode
    ? [...REQUIRED_COST_ITEM_TYPES_COMMON, ...REQUIRED_COST_ITEM_TYPES_SIMPLE_ONLY]
    : REQUIRED_COST_ITEM_TYPES_COMMON;

  /** 現在のモードで必須となる指標項目タイプ */
  const requiredIndicatorItemTypes: RationalizationProfitLossItemType[] = isSimpleMode
    ? REQUIRED_INDICATOR_ITEM_TYPES_SIMPLE_ONLY
    : [];

  /** 必須費用項目を補完する（既に存在する場合はスキップ） */
  const ensureRequiredCostItems = (items: RationalizationProfitLossItem[]): RationalizationProfitLossItem[] => {
    const result = [...items];
    requiredCostItemTypes.forEach((type) => {
      if (!result.some((item) => item.itemType === type)) {
        result.push({
          id: uuidv4(),
          itemType: type,
          unitType: "currency",
          beforeMonthlyData: {},
          afterMonthlyData: {},
        });
      }
    });
    return result;
  };

  /** 必須指標項目を補完する（既に存在する場合はスキップ） */
  const ensureRequiredIndicatorItems = (items: RationalizationProfitLossItem[]): RationalizationProfitLossItem[] => {
    const result = [...items];
    requiredIndicatorItemTypes.forEach((type) => {
      if (!result.some((item) => item.itemType === type)) {
        result.push({
          id: uuidv4(),
          itemType: type,
          unitType: getDefaultUnitTypeByItemType(type),
          beforeMonthlyData: {},
          afterMonthlyData: {},
        });
      }
    });
    return result;
  };

  const defaultRationalizationSummary: RationalizationSummaryInputData = {
    processId: processId,
    itemNo: itemNo,
    fixedAssetNumber: "",
    operatingDays: 0,
    workTime: "00:00",
    shiftSystem: ShiftSystem.OneShiftSystem.code,
    partsCount: 1,
    cumulativeCost: 0,
    selectedPartNo: "",
    productionCapacityByPartNo: {},
    rationalizationReason: "",
    productionPerEquipment: "",
    productionPerPerson: "",
    nonOperationMeasures: "",
    nonProcessingMeasures: "",
    currentEquipmentHandling: "",
    laborSavingPlan: "",
    expectedEffect: "",
    indicatorItems: [],
    costItems: ensureRequiredCostItems([]),
  };

  /** デフォルトの生産能力データ */
  const defaultProductionCapacityData: RationalizationProductionCapacityData = {
    equipmentCount: 0,
    yield: 0,
    nonWorkingTime: 0,
    cycleTime: 0,
    personnelPerDay: 0,
    productionVolume: undefined,
    productionPerEquipment: undefined,
    productionPerPerson: undefined,
  };
  /** ローカル合理化前後サマリーデータ */
  const [localRationalizationSummary, setLocalRationalizationSummary] = useState<RationalizationSummaryInputData>(defaultRationalizationSummary);
  const TITLE = t('components.RationalizationSummaryInputPopover.title')

  /** 指標グループの項目選択肢（翻訳付き） */
  const INDICATOR_ITEM_OPTIONS = useMemo((): ItemOption[] => [
    { value: "", label: t('components.RationalizationSummaryInputPopover.options.indicator.selectPlaceholder'), unit: "pcs" },
    { value: "productionQuantity", label: t('components.RationalizationSummaryInputPopover.options.indicator.productionQuantity'), unit: "pcs" },
    { value: "salesQuantity", label: t('components.RationalizationSummaryInputPopover.options.indicator.salesQuantity'), unit: "pcs" },
    { value: "custom", label: t('components.RationalizationSummaryInputPopover.options.indicator.custom'), unit: "currency" },
  ], [t]);

  /** 費用グループの項目選択肢（翻訳付き） */
  const COST_ITEM_OPTIONS = useMemo((): ItemOption[] => [
    { value: "", label: t('components.RationalizationSummaryInputPopover.options.cost.selectPlaceholder'), unit: "pcs" },
    { value: "laborCost", label: t('components.RationalizationSummaryInputPopover.options.cost.laborCost'), unit: "currency" },
    { value: "repairCost", label: t('components.RationalizationSummaryInputPopover.options.cost.repairCost'), unit: "currency" },
    { value: "consumablesCost", label: t('components.RationalizationSummaryInputPopover.options.cost.consumablesCost'), unit: "currency" },
    { value: "maintenanceCost", label: t('components.RationalizationSummaryInputPopover.options.cost.maintenanceCost'), unit: "currency" },
    { value: "scrapCost", label: t('components.RationalizationSummaryInputPopover.options.cost.scrapCost'), unit: "currency" },
    { value: "targetEquipmentDepreciation", label: t('components.RationalizationSummaryInputPopover.options.cost.targetEquipmentDepreciation'), unit: "currency" },
    { value: "otherDepreciation", label: t('components.RationalizationSummaryInputPopover.options.cost.otherDepreciation'), unit: "currency" },
    { value: "custom", label: t('components.RationalizationSummaryInputPopover.options.cost.custom'), unit: "currency" },
  ], [t]);

  /** 全ての項目選択肢（表示名取得用） */
  const ALL_ITEM_OPTIONS = useMemo((): ItemOption[] => [
    ...INDICATOR_ITEM_OPTIONS.filter(opt => opt.value !== ""),
    ...COST_ITEM_OPTIONS.filter(opt => opt.value !== ""),
  ], [INDICATOR_ITEM_OPTIONS, COST_ITEM_OPTIONS]);

  const WIDTH_ITEM_NAME = 240;
  const WIDTH_OPERATE = 65;
  const WIDTH_KBN = 100;
  const WIDTH_VALUE = 150;

  /** 指標グループ項目（動的） */
  const [indicatorItems, setIndicatorItems] = useState<RationalizationProfitLossItem[]>(() => ensureRequiredIndicatorItems([]));
  /** 指標グループ項目選択値 */
  const [selectedIndicatorItemType, setSelectedIndicatorItemType] = useState<RationalizationProfitLossItemType | "">("");
  /** 費用グループ項目（動的） */
  const [costItems, setCostItems] = useState<RationalizationProfitLossItem[]>(() => ensureRequiredCostItems([]));
  /** 費用グループ項目選択値 */
  const [selectedCostItemType, setSelectedCostItemType] = useState<RationalizationProfitLossItemType | "">("");
  /** 費用グループ選択通貨（デフォルトはローカル通貨） */
  const [selectedCostCurrency, setSelectedCostCurrency] = useState<string>(localCurrency || "");
  const prevCostCurrencyRef = useRef<string>(localCurrency || "");
  /** 設備データキャッシュ（モーダルを開いている間は再利用して通貨変更時のAPIコールを省略） */
  const equipmentCacheRef = useRef<EquipmentType[] | null>(null);
  /** 自由入力モーダルの開閉状態 */
  const [isCustomItemModalOpen, setIsCustomItemModalOpen] = useState(false);
  /** 自由入力モーダル：対象グループ（"indicator" | "cost"） */
  const [customItemTargetGroup, setCustomItemTargetGroup] = useState<"indicator" | "cost">("indicator");
  /** 自由入力モーダル：項目名 */
  const [customItemName, setCustomItemName] = useState("");
  /** 自由入力モーダル：単位 */
  const [customUnit, setCustomUnit] = useState("");
  /** 自由入力モーダル：編集対象の項目ID（新規追加時はnull） */
  const [editingCustomItemId, setEditingCustomItemId] = useState<string | null>(null);
  /** GoogleSpread処理中フラグ */
  const [isLoading, setIsLoading] = useState(false);
  /** アクティブなスプレッドシートID（指標グループ） */
  const [activeIndicatorSpreadsheetId, setActiveIndicatorSpreadsheetId] = useState<string | null>(null);
  /** アクティブなスプレッドシートID（費用グループ） */
  const [activeCostSpreadsheetId, setActiveCostSpreadsheetId] = useState<string | null>(null);
  /** スプレッドシートウィンドウが閉じられたか（指標グループ） */
  const [isIndicatorSpreadsheetWindowClosed, setIsIndicatorSpreadsheetWindowClosed] = useState(false);
  /** スプレッドシートウィンドウが閉じられたか（費用グループ） */
  const [isCostSpreadsheetWindowClosed, setIsCostSpreadsheetWindowClosed] = useState(false);
  /** スプレッドシートウィンドウ参照（指標グループ） */
  const [indicatorSpreadsheetWindow, setIndicatorSpreadsheetWindow] = useState<Window | null>(null);
  /** スプレッドシートウィンドウ参照（費用グループ） */
  const [costSpreadsheetWindow, setCostSpreadsheetWindow] = useState<Window | null>(null);
  /** バリデーションエラーセル（指標グループ）: Set<"itemId:month:category"> or Set<"itemId:item"> */
  const [indicatorErrorCells, setIndicatorErrorCells] = useState<Set<string>>(new Set());
  /** バリデーションエラーセル（費用グループ） */
  const [costErrorCells, setCostErrorCells] = useState<Set<string>>(new Set());
  /** ホバー中の項目ID（指標グループ） */
  const [hoveredIndicatorItemId, setHoveredIndicatorItemId] = useState<string | null>(null);
  /** ホバー中の項目ID（費用グループ） */
  const [hoveredCostItemId, setHoveredCostItemId] = useState<string | null>(null);

  /** GoogleAPI インスタンス */
  const googleAPI: GoogleAPI = new GoogleAPI();
  /** SpreadSheet保存パス */
  const SPREAD_SHEET_PATH = "CEAS/SpreadSheet/RationalizationSummary";

  /**
   * 年月リストを生成（量産開始日～EOL予定日）
   */
  const monthList = useMemo((): string[] => {
    if (!massProductionStartDate || !eolDate) return [];
    const start = dayjs(massProductionStartDate).startOf("month");
    const end = dayjs(eolDate).startOf("month");
    if (!start.isValid() || !end.isValid() || start.isAfter(end)) return [];

    const months: string[] = [];
    let current = start;
    while (current.isBefore(end) || current.isSame(end, "month")) {
      months.push(current.format("YYYY-MM"));
      current = current.add(1, "month");
    }
    return months;
  }, [massProductionStartDate, eolDate]);

  /**
   * 年月を「YYYY年M月」形式でフォーマット
   */
  const formatMonthDisplay = (month: string): string => {
    const d = dayjs(month);
    if (!d.isValid()) return month;
    return d.format(t('common.dateFormat.dayjsYearMonth'));
  };

  /**
   * 項目の表示名を取得
   */
  const getItemDisplayName = (item: RationalizationProfitLossItem): string => {
    if (item.itemType === "custom") {
      return item.customItemName || t('components.RationalizationSummaryInputPopover.options.indicator.custom');
    }
    const option = ALL_ITEM_OPTIONS.find(opt => opt.value === item.itemType);
    return option?.label || "";
  };

  /**
   * 項目の単位を取得
   * @param item 項目
   * @param group グループ種別（"indicator" | "cost"）
   */
  const getItemUnit = (item: RationalizationProfitLossItem, group: "indicator" | "cost"): string => {
    // 費用グループの場合は選択された通貨を使用
    if (group === "cost") {
      const currency = currencyData.find(c => c.currencyId === selectedCostCurrency);
      return currency?.currencyName || "";
    }
    // 指標グループの場合
    if (item.itemType === "custom") {
      return item.customUnit || "";
    }
    const option = ALL_ITEM_OPTIONS.find(opt => opt.value === item.itemType);
    return option?.unit || "";
  };

  /**
   * 項目を作成するヘルパー関数
   */
  const createItem = (
    itemType: RationalizationProfitLossItemType,
    customItemNameValue?: string,
    customUnitValue?: string
  ): RationalizationProfitLossItem => {
    const option = ALL_ITEM_OPTIONS.find(opt => opt.value === itemType);
    const newItem: RationalizationProfitLossItem = {
      id: uuidv4(),
      itemType,
      customItemName: customItemNameValue,
      unitType: itemType === "custom" ? "custom" : (option?.unit ?? "currency"),
      customUnit: customUnitValue,
      beforeMonthlyData: {},
      afterMonthlyData: {},
    };
    // 月別データの初期化
    monthList.forEach(month => {
      newItem.beforeMonthlyData[month] = 0;
      newItem.afterMonthlyData[month] = 0;
    });
    return newItem;
  };

  /** グループ種別 */
  type GroupType = "indicator" | "cost";

  /**
   * グループに応じたstate操作を取得するヘルパー
   */
  const getGroupStateHandlers = (group: GroupType) => ({
    items: group === "indicator" ? indicatorItems : costItems,
    setItems: group === "indicator" ? setIndicatorItems : setCostItems,
    setSelectedItemType: group === "indicator" ? setSelectedIndicatorItemType : setSelectedCostItemType,
    errorCells: group === "indicator" ? indicatorErrorCells : costErrorCells,
    setErrorCells: group === "indicator" ? setIndicatorErrorCells : setCostErrorCells,
    activeSpreadsheetId: group === "indicator" ? activeIndicatorSpreadsheetId : activeCostSpreadsheetId,
    setActiveSpreadsheetId: group === "indicator" ? setActiveIndicatorSpreadsheetId : setActiveCostSpreadsheetId,
    spreadsheetWindow: group === "indicator" ? indicatorSpreadsheetWindow : costSpreadsheetWindow,
    setSpreadsheetWindow: group === "indicator" ? setIndicatorSpreadsheetWindow : setCostSpreadsheetWindow,
    isSpreadsheetWindowClosed: group === "indicator" ? isIndicatorSpreadsheetWindowClosed : isCostSpreadsheetWindowClosed,
    setIsSpreadsheetWindowClosed: group === "indicator" ? setIsIndicatorSpreadsheetWindowClosed : setIsCostSpreadsheetWindowClosed,
    itemOptions: group === "indicator" ? INDICATOR_ITEM_OPTIONS : COST_ITEM_OPTIONS,
    displayName: group === "indicator" ? "指標" : "費用",
  });

  /**
   * 項目を追加（共通）
   */
  const addItem = (
    group: GroupType,
    itemType: RationalizationProfitLossItemType,
    customItemNameValue?: string,
    customUnitValue?: string
  ) => {
    const newItem = createItem(itemType, customItemNameValue, customUnitValue);
    const { setItems, setSelectedItemType } = getGroupStateHandlers(group);
    setItems(prev => [...prev, newItem]);
    setSelectedItemType("");
  };

  /**
   * 項目を削除（共通）
   */
  const removeItem = (group: GroupType, itemId: string) => {
    const { setItems } = getGroupStateHandlers(group);
    setItems(prev => prev.filter(item => item.id !== itemId));
  };

  /**
   * 項目を上下に並び替える（共通）
   * @param direction "up" | "down"
   */
  const moveItem = (group: GroupType, itemId: string, direction: "up" | "down") => {
    const { setItems } = getGroupStateHandlers(group);
    setItems(prev => {
      const index = prev.findIndex(item => item.id === itemId);
      if (index < 0) return prev;
      const targetIndex = direction === "up" ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next;
    });
  };

  /**
   * 項目の月別データを更新（共通）
   */
  const updateItemMonthlyData = (
    group: GroupType,
    itemId: string,
    category: "before" | "after",
    month: string,
    value: number
  ) => {
    const { setItems } = getGroupStateHandlers(group);
    setItems(prev =>
      prev.map(item => {
        if (item.id !== itemId) return item;
        if (category === "before") {
          return { ...item, beforeMonthlyData: { ...item.beforeMonthlyData, [month]: value } };
        } else {
          return { ...item, afterMonthlyData: { ...item.afterMonthlyData, [month]: value } };
        }
      })
    );
  };

  /**
   * 自由入力モーダルの確定ハンドラー
   */
  const handleCustomItemConfirm = () => {
    if (customItemName.trim() && customUnit.trim()) {
      const { setItems, setErrorCells } = getGroupStateHandlers(customItemTargetGroup);

      if (editingCustomItemId) {
        // 編集モード：既存項目を更新
        setItems(prev =>
          prev.map(item =>
            item.id === editingCustomItemId
              ? { ...item, customItemName: customItemName.trim(), customUnit: customUnit.trim() }
              : item
          )
        );
        // 単位が設定されたので、項目エラーを解除
        setErrorCells(prev => {
          const newSet = new Set(prev);
          newSet.delete(`${editingCustomItemId}:item`);
          return newSet;
        });
      } else {
        // 新規追加モード
        addItem(customItemTargetGroup, "custom", customItemName.trim(), customUnit.trim());
      }
      setCustomItemName("");
      setCustomUnit("");
      setIsCustomItemModalOpen(false);
      setEditingCustomItemId(null);
    }
  };

  /**
   * 自由入力モーダルのキャンセルハンドラー
   */
  const handleCustomItemCancel = () => {
    setCustomItemName("");
    setCustomUnit("");
    setIsCustomItemModalOpen(false);
    setEditingCustomItemId(null);
    const { setSelectedItemType } = getGroupStateHandlers(customItemTargetGroup);
    setSelectedItemType("");
  };

  /**
   * 自由入力項目の編集開始ハンドラー（共通）
   */
  const handleEditCustomItem = (group: GroupType, item: RationalizationProfitLossItem) => {
    if (item.itemType !== "custom") return;
    setEditingCustomItemId(item.id);
    setCustomItemName(item.customItemName || "");
    setCustomUnit(item.customUnit || (group === "cost" ? "通貨" : ""));
    setCustomItemTargetGroup(group);
    setIsCustomItemModalOpen(true);
  };

  /**
   * GoogleSpreadボタン押下（共通）
   */
  const handleGroupGoogleSpread = async (group: GroupType) => {
    const { items } = getGroupStateHandlers(group);
    await handleGoogleSpread(group, items);
  };

  /**
   * GoogleSpreadボタン押下共通処理
   * @param groupName グループ名（"indicator" | "cost"）
   * @param items 対象の項目リスト
   */
  const handleGoogleSpread = async (groupName: "indicator" | "cost", items: RationalizationProfitLossItem[]) => {
    const functionName = "handleGoogleSpread";
    const groupDisplayName = groupName === "indicator" ? "指標" : "費用";

    try {
      setIsLoading(true);

      // ポップアップブロック回避：非同期処理より前にユーザー操作起因でウィンドウを確保
      // 既存ウィンドウが生きていれば再利用、なければ新規ブランクタブを開く
      const existingWindow = groupName === "indicator" ? indicatorSpreadsheetWindow : costSpreadsheetWindow;
      let newWindow: Window | null = (existingWindow && !existingWindow.closed)
        ? existingWindow
        : window.open("", "_blank");

      // Googleアカウント認証
      if (!await googleAPI.signIn()) {
        throw new Error("signIn error");
      }

      // マイドライブに作成（SpreadSheet/RationalizationSummary/[projectId]）
      const spreadSheetPath = `${SPREAD_SHEET_PATH}/${encodeURIComponent(projectId)}`;
      const sharedDriveName: string | null = null; // マイドライブに作成

      // スプレッドシート親フォルダID取得
      const parentId = await googleAPI.createFolderPath(spreadSheetPath, sharedDriveName);

      // ファイル名: [projectId]-[processId]-[グループ名]
      const fileName = `${projectId}-${processId || "unknown"}-${groupDisplayName}`;
      const spreadsheetId = await googleAPI.createSpreadSheet(fileName, parentId!, sharedDriveName);

      // シート名
      const sheetName = groupDisplayName;

      // シート追加（デフォルトシートは削除される）
      await googleAPI.addSheets(spreadsheetId, [sheetName]);
      console.log(`${functionName} add sheet OK`);

      // シートID情報を取得
      const spreadsheetInfo = await googleAPI.getSpreadSheetInfo(spreadsheetId);
      const sheetId = spreadsheetInfo.sheets?.find((s: any) => s.properties?.title === sheetName)?.properties?.sheetId ?? 0;
      // 前回設定した書式・入力規則を初期化（列構成変更時に古い設定が残らないようにする。値の書き込みより前に実行）
      await googleAPI.resetSheetFormatAndValidation(spreadsheetId, [sheetId]);

      // 既存のセル値クリア
      await googleAPI.clear(spreadsheetId, sheetName);

      // ヘッダー行を作成: [項目, 区分, 2025年5月, 2025年6月, ...]
      const headerRow = ["項目", "区分", ...monthList.map(m => formatMonthDisplay(m))];

      // データ行を作成
      const dataRows: string[][] = [];

      // 項目の選択肢リスト（入力規則用）
      const itemOptions = groupName === "indicator"
        ? INDICATOR_ITEM_OPTIONS.filter(opt => opt.value !== "" && opt.value !== "custom").map(opt => opt.label)
        : COST_ITEM_OPTIONS.filter(opt => opt.value !== "" && opt.value !== "custom").map(opt => opt.label);

      items.forEach((item) => {
        const itemName = getItemDisplayName(item);

        // 合理化前の行
        const beforeRow = [itemName, "合理化前", ...monthList.map(m => String(item.beforeMonthlyData[m] ?? 0))];
        dataRows.push(beforeRow);

        // 合理化後の行（項目名は空、rowSpan的な表現）
        const afterRow = ["", "合理化後", ...monthList.map(m => String(item.afterMonthlyData[m] ?? 0))];
        dataRows.push(afterRow);
      });

      // シートにデータを設定
      const allRows = [headerRow, ...dataRows];
      await googleAPI.spreadSheetSetValue(spreadsheetId, sheetName, 1, 1, allRows);
      console.log(`${functionName} ${groupDisplayName} sheet set value OK`);

      // 入力規則設定（項目列）
      // 項目名列（A列）に対してプルダウンを一括設定
      // データ行は2行目から開始し、各項目は2行ずつ（合理化前/後）
      if (items.length > 0) {
        const startRow = 2; // データ開始行（ヘッダーの次）
        const endRow = 1 + (items.length * 2); // 最終データ行
        await googleAPI.spreadSheetSetDataValidation(spreadsheetId, [{
          sheetId: sheetId,
          startRow: startRow,
          endRow: endRow,
          startCol: 1, // A列
          endCol: 1,
          values: itemOptions,
          strict: false, // 選択肢以外も許可
          showDropdown: true,
        }]);
      }

      // スプレッドシートIDを保存
      if (groupName === "indicator") {
        setActiveIndicatorSpreadsheetId(spreadsheetId);
        setIndicatorErrorCells(new Set()); // エラー表示クリア
      } else {
        setActiveCostSpreadsheetId(spreadsheetId);
        setCostErrorCells(new Set()); // エラー表示クリア
      }

      // 事前確保したウィンドウをスプレッドシートに遷移
      const spreadSheetUrl = googleAPI.getOpenSpradSheetUrl(spreadsheetId);
      if (newWindow) {
        newWindow.location.href = spreadSheetUrl;
        newWindow.focus();

        // ウィンドウ参照を保存し、閉じられていない状態に設定
        if (groupName === "indicator") {
          setIndicatorSpreadsheetWindow(newWindow);
          setIsIndicatorSpreadsheetWindowClosed(false);
        } else {
          setCostSpreadsheetWindow(newWindow);
          setIsCostSpreadsheetWindowClosed(false);
        }

        // GoogleSpreadシートウィンドウを手動で閉じられた場合
        const interval = setInterval(() => {
          if (newWindow?.closed) {
            clearInterval(interval); // 監視を停止
            if (groupName === "indicator") {
              setIsIndicatorSpreadsheetWindowClosed(true);
            } else {
              setIsCostSpreadsheetWindowClosed(true);
            }
          }
        }, 500); // 500ms ごとに監視
      } else {
        console.warn("A pop-up from Google Sheets was blocked.");
      }

    } catch (error) {
      console.error(`${functionName} GoogleSpread Error.`, error);

      toast({
        title: TITLE,
        description: t('components.RationalizationSummaryInputPopover.message.googleSpreadUnavailable'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * データ連携ボタン押下（共通）
   */
  const handleGroupDataLink = async (group: GroupType) => {
    const { activeSpreadsheetId } = getGroupStateHandlers(group);
    await handleDataLink(group, activeSpreadsheetId);
  };

  /**
   * データ連携ボタン押下共通処理
   * @param groupName グループ名（"indicator" | "cost"）
   * @param spreadsheetId スプレッドシートID
   */
  const handleDataLink = async (groupName: "indicator" | "cost", spreadsheetId: string | null) => {
    const functionName = "handleDataLink";
    const groupDisplayName = groupName === "indicator" ? "指標" : "費用";

    if (!spreadsheetId) {
      toast({
        title: TITLE,
        description: t('components.RationalizationSummaryInputPopover.message.pressGoogleSpreadFirst'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsLoading(true);

      // Googleアカウント認証
      if (!await googleAPI.signIn()) {
        throw new Error("signIn error");
      }

      // シート名
      const sheetName = groupDisplayName;

      // スプレッドシートからデータ取得
      const sheetData = await googleAPI.spreadSheetGetValues(spreadsheetId, sheetName);

      if (!sheetData.values || sheetData.values.length < 2) {
        toast({
          title: TITLE,
          description: t('components.RationalizationSummaryInputPopover.message.noSpreadsheetData'),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // ヘッダー行（1行目）: [項目, 区分, 2025年5月, 2025年6月, ...]
      const headerRow = sheetData.values[0] as string[];

      // 年月列のインデックスマッピング（2列目以降が年月）
      const monthColumnMap: Record<string, number> = {};
      for (let i = 2; i < headerRow.length; i++) {
        const monthHeader = headerRow[i];
        // 「YYYY年M月」形式から「YYYY-MM」形式に変換
        const match = monthHeader?.match(/(\d{4})年(\d{1,2})月/);
        if (match) {
          const year = match[1];
          const month = match[2].padStart(2, '0');
          const yearMonth = `${year}-${month}`;
          monthColumnMap[yearMonth] = i;
        }
      }

      // データ行（2行目以降）を解析
      const dataRows = sheetData.values.slice(1);

      // 新しい項目リストとエラーセット
      const newItems: RationalizationProfitLossItem[] = [];
      const errorCells = new Set<string>();

      // 項目の選択肢マップ（ラベル → itemType）
      const itemLabelToType: Record<string, RationalizationProfitLossItemType> = {};
      const optionsList = groupName === "indicator" ? INDICATOR_ITEM_OPTIONS : COST_ITEM_OPTIONS;
      optionsList.forEach(opt => {
        if (opt.value !== "") {
          itemLabelToType[opt.label] = opt.value as RationalizationProfitLossItemType;
        }
      });

      // データ行を2行ずつ処理（合理化前/後）
      let rowIndex = 0;
      while (rowIndex < dataRows.length) {
        const beforeRow = dataRows[rowIndex];
        const afterRow = dataRows[rowIndex + 1];

        if (!beforeRow || beforeRow.length < 2) {
          rowIndex += 2;
          continue;
        }

        const itemName = String(beforeRow[0] || "").trim();
        const beforeCategory = String(beforeRow[1] || "").trim();
        const afterCategory = afterRow ? String(afterRow[1] || "").trim() : "";

        // 項目名が空白の場合はデータ連携対象外
        if (!itemName) {
          rowIndex += 2;
          continue;
        }

        // 区分チェック（合理化前/後のパターンのみ）
        if (beforeCategory !== "合理化前" || afterCategory !== "合理化後") {
          rowIndex += 2;
          continue;
        }

        // 項目タイプを判定
        let itemType: RationalizationProfitLossItemType;
        let customItemName: string | undefined;
        let customUnit: string | undefined;
        let hasItemError = false;

        if (itemLabelToType[itemName]) {
          // 選択項目
          itemType = itemLabelToType[itemName];
        } else {
          // 自由入力項目
          itemType = "custom";
          customItemName = itemName;
          // 費用グループは通貨固定、指標グループは単位が必要だがSpreadSheetからは取得できないため空
          if (groupName === "cost") {
            customUnit = "通貨";
          } else {
            // 指標グループの自由入力: 既存の項目から単位を探す
            const existingItem = (groupName === "indicator" ? indicatorItems : costItems)
              .find(item => item.itemType === "custom" && item.customItemName === itemName);
            if (existingItem?.customUnit) {
              customUnit = existingItem.customUnit;
            } else {
              // 単位が未設定 → エラー
              customUnit = "";
              hasItemError = true;
            }
          }
        }

        // 新しい項目を作成
        const newItemId = uuidv4();
        const beforeMonthlyData: Record<string, number> = {};
        const afterMonthlyData: Record<string, number> = {};

        // 年月データを取得
        monthList.forEach((yearMonth) => {
          const colIndex = monthColumnMap[yearMonth];
          if (colIndex !== undefined) {
            // 合理化前の値
            const beforeValue = beforeRow[colIndex];
            const beforeNum = parseFloat(String(beforeValue ?? "").replace(/,/g, ""));
            if (isNaN(beforeNum)) {
              beforeMonthlyData[yearMonth] = 0;
              errorCells.add(`${newItemId}:${yearMonth}:before`);
            } else {
              beforeMonthlyData[yearMonth] = beforeNum;
            }

            // 合理化後の値
            if (afterRow) {
              const afterValue = afterRow[colIndex];
              const afterNum = parseFloat(String(afterValue ?? "").replace(/,/g, ""));
              if (isNaN(afterNum)) {
                afterMonthlyData[yearMonth] = 0;
                errorCells.add(`${newItemId}:${yearMonth}:after`);
              } else {
                afterMonthlyData[yearMonth] = afterNum;
              }
            }
          } else {
            // 対応する年月列がない場合は0
            beforeMonthlyData[yearMonth] = 0;
            afterMonthlyData[yearMonth] = 0;
          }
        });

        // 項目エラーを記録
        if (hasItemError) {
          errorCells.add(`${newItemId}:item`);
        }

        const newItem: RationalizationProfitLossItem = {
          id: newItemId,
          itemType,
          customItemName,
          unitType: itemType === "custom" ? "custom" : (optionsList.find(opt => opt.value === itemType)?.unit === "pcs" ? "pcs" : "currency"),
          customUnit,
          beforeMonthlyData,
          afterMonthlyData,
        };

        newItems.push(newItem);
        rowIndex += 2;
      }

      // 項目リストとエラーセットを更新
      if (groupName === "indicator") {
        setIndicatorItems(newItems);
        setIndicatorErrorCells(errorCells);
      } else {
        const itemsWithFlag = ensureRequiredCostItems(newItems).map(item => {
          if (item.itemType !== "targetEquipmentDepreciation") return item;
          const prevItem = costItems.find(prev => prev.itemType === "targetEquipmentDepreciation");
          const roundVal = (v: number) => Math.round(v * 100) / 100;
          const prevBeforeEdited = prevItem?.isManuallyEditedBefore ?? false;
          const prevAfterEdited = prevItem?.isManuallyEditedAfter ?? false;
          const allMonths = Object.keys({ ...prevItem?.beforeMonthlyData, ...item.beforeMonthlyData });
          const beforeChanged = prevBeforeEdited || (prevItem
            ? allMonths.some(month =>
              roundVal(item.beforeMonthlyData[month] ?? 0) !== roundVal(prevItem.beforeMonthlyData[month] ?? 0)
            )
            : false);
          const afterAllMonths = Object.keys({ ...prevItem?.afterMonthlyData, ...item.afterMonthlyData });
          const afterChanged = prevAfterEdited || (prevItem
            ? afterAllMonths.some(month =>
              roundVal(item.afterMonthlyData[month] ?? 0) !== roundVal(prevItem.afterMonthlyData[month] ?? 0)
            )
            : false);
          return { ...item, isManuallyEditedBefore: beforeChanged, isManuallyEditedAfter: afterChanged };
        });
        setCostItems(itemsWithFlag);
        setCostErrorCells(errorCells);
      }

      // 結果トースト
      if (errorCells.size > 0) {
        toast({
          title: TITLE,
          description: t('components.RationalizationSummaryInputPopover.message.dataLinkCompleteWithError', { count: errorCells.size }),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: TITLE,
          description: t('components.RationalizationSummaryInputPopover.message.dataLinkComplete'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }

      console.log(`${functionName} ${groupDisplayName} data link OK. items: ${newItems.length}, errors: ${errorCells.size}`);

    } catch (error) {
      console.error(`${functionName} DataLink Error.`, error);

      toast({
        title: TITLE,
        description: t('components.RationalizationSummaryInputPopover.message.dataLinkFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 項目選択変更ハンドラー（共通）
   */
  const handleItemTypeChange = (group: GroupType, value: RationalizationProfitLossItemType | "") => {
    if (!value) return;

    const { setSelectedItemType } = getGroupStateHandlers(group);

    if (value === "custom") {
      setSelectedItemType(value);
      setCustomItemTargetGroup(group);
      if (group === "cost") {
        setCustomUnit("通貨"); // 費用グループは通貨固定
      }
      setIsCustomItemModalOpen(true);
    } else {
      addItem(group, value);
      setSelectedItemType("");
    }
  };

  /** 現在選択中のPartNoのデータ（アクセス用ヘルパー） */
  const currentPartNoData = useMemo(() => {
    const selectedPartNo = localRationalizationSummary.selectedPartNo;
    if (!selectedPartNo) {
      return {
        recentActual: { ...defaultProductionCapacityData },
        theoretical: { ...defaultProductionCapacityData },
        investmentPlan: { ...defaultProductionCapacityData },
      };
    }
    return localRationalizationSummary.productionCapacityByPartNo?.[selectedPartNo] || {
      recentActual: { ...defaultProductionCapacityData },
      theoretical: { ...defaultProductionCapacityData },
      investmentPlan: { ...defaultProductionCapacityData },
    };
  }, [localRationalizationSummary.selectedPartNo, localRationalizationSummary.productionCapacityByPartNo]);

  /** 通信オブジェクト */
  const WebAPI = useContext(WebAPIContext);
  /** トースト表示用 */
  const toast = useToast();
  /** Popoverの開閉状態 */
  const [isOpen, setIsOpen] = useState(false);
  /**保存ボタン活性制御 */
  const isSaving = isSave;

  /**
   * Popoverが閉じられた時にSpreadSheetウィンドウを閉じる
   */
  useEffect(() => {
    if (!isOpen) {
      // 指標グループのSpreadSheetウィンドウを閉じる
      if (indicatorSpreadsheetWindow && !indicatorSpreadsheetWindow.closed) {
        indicatorSpreadsheetWindow.close();
      }
      // 費用グループのSpreadSheetウィンドウを閉じる
      if (costSpreadsheetWindow && !costSpreadsheetWindow.closed) {
        costSpreadsheetWindow.close();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  /**
   * 工程の設備データを取得し targetEquipmentDepreciation の月別償却費を自動計算する
   */
  const calcTargetEquipmentDepreciation = async (options?: { updateBefore?: boolean; updateAfter?: boolean; targetCurrency?: string }) => {
    const { updateBefore = true, updateAfter = true, targetCurrency } = options ?? {};
    if (!processId || !massProductionStartDate || monthList.length === 0) return;

    let allEquipments: EquipmentType[];
    if (equipmentCacheRef.current) {
      allEquipments = equipmentCacheRef.current;
    } else {
      const response = await WebAPI.request({
        query: `
          query getEquipmentByProcessId($projectId: String, $processId: String) {
            getEquipmentByProcessId(projectId: $projectId, processId: $processId) {
              projectId
              amount
              unitPrice
              currencyId
              depreciationPeriod
              depreciationStartMonth
              acceptanceMonth
            }
          }`,
        variables: { projectId, processId }
      });
      if (!response?.data?.getEquipmentByProcessId) return;
      allEquipments = (response.data.getEquipmentByProcessId as EquipmentType[]).map(
        (e: EquipmentType) => ({ ...e, isReadonly: e.projectId !== projectId })
      );
      equipmentCacheRef.current = allEquipments;
    }

    calcDepreciationFromCache(targetCurrency ?? selectedCostCurrency, allEquipments, { updateBefore, updateAfter });
  };

  /**
   * キャッシュ済みの設備データから月別償却費を同期計算し setCostItems を呼ぶ
   * イベントハンドラーから呼ぶことで setSelectedCostCurrency とバッチ処理され、フラッシュを防ぐ
   */
  const calcDepreciationFromCache = (
    targetCurrencyId: string,
    equipments: EquipmentType[],
    options: { updateBefore: boolean; updateAfter: boolean }
  ) => {
    if (!massProductionStartDate || monthList.length === 0) return;
    const { updateBefore, updateAfter } = options;
    const currency = currencyData.find(c => c.currencyId === targetCurrencyId);
    const targetCurrencyName = currency?.currencyName ?? targetCurrencyId;
    const maxYears = Math.ceil(monthList.length / 12) + 1;

    const toMonthlyRecord = (equips: EquipmentType[]): Record<string, number> => {
      const converted = convertEquipmentData(equips, targetCurrencyName, currencyData);
      const depreciation = calcEquipmentDepreciationByYearMonth(
        maxYears, converted, hasDecimal(targetCurrencyName), massProductionStartDate, false
      );
      const result: Record<string, number> = {};
      let current = dayjs(massProductionStartDate);
      let fiscalYear = 1;
      monthList.forEach((monthStr, i) => {
        if (i > 0 && current.month() + 1 === 4) fiscalYear++;
        const calMonth = current.month() + 1;
        const found = depreciation.find(d => d.year === fiscalYear && d.month === calMonth);
        result[monthStr] = found?.value.toNumber() ?? 0;
        current = current.add(1, "month");
      });
      return result;
    };

    const afterMonthlyData = toMonthlyRecord(equipments.filter(e => !e.isReadonly));
    const beforeMonthlyData = toMonthlyRecord(equipments.filter(e => e.isReadonly));

    setCostItems(prev => prev.map(item =>
      item.itemType === "targetEquipmentDepreciation"
        ? {
          ...item,
          ...(updateBefore ? { beforeMonthlyData, isManuallyEditedBefore: false } : {}),
          ...(updateAfter ? { afterMonthlyData, isManuallyEditedAfter: false } : {}),
        }
        : item
    ));
  };

  useEffect(() => {
    if (!isOpen) return;
    // ポップオーバーを開いた時に設備データキャッシュをクリア（最新データを取得するため）
    equipmentCacheRef.current = null;
    // ポップオーバーを開いた時に前通貨refを現在の通貨で初期化
    prevCostCurrencyRef.current = selectedCostCurrency;
    // 保存済みデータがある場合は上書きしない（ただしキャッシュは常に温める）
    const targetItem = costItems.find(item => item.itemType === "targetEquipmentDepreciation");
    const hasExistingData = targetItem && Object.keys(targetItem.beforeMonthlyData).length > 0;
    if (!hasExistingData) {
      calcTargetEquipmentDepreciation().catch(err => console.error("償却費自動計算エラー:", err));
    } else {
      // 表示値は変えないがキャッシュを温めておく（通貨変更を即時にするため）
      calcTargetEquipmentDepreciation({ updateBefore: false, updateAfter: false }).catch(err => console.error("償却費キャッシュ取得エラー:", err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  /**
   * PartNo関連の入力フィールド値を生成
   * @param partNoData 選択されたPartNoのデータ または null
   * @param defaultYield デフォルト歩留（productionLineDataから取得）
   * @param defaultCycleTime デフォルトサイクルタイム（productionLineDataから取得）
   * @returns PartNo関連の入力フィールド値オブジェクト
   */
  const getPartNoInputValues = (
    partNoData: RationalizationProductionCapacityDataByPartNo | null,
    defaultYield: number = 0,
    defaultCycleTime: number = 0
  ): { [key: string]: string } => ({
    // 生産能力（直近3ヶ月平均実績）
    "recentActual.equipmentCount": String(partNoData?.recentActual?.equipmentCount ?? 0),
    "recentActual.yield": (partNoData?.recentActual?.yield ?? 0).toFixed(2),
    "recentActual.nonWorkingTime": (partNoData?.recentActual?.nonWorkingTime ?? 0).toFixed(2),
    "recentActual.cycleTime": (partNoData?.recentActual?.cycleTime ?? 0).toFixed(1),
    "recentActual.personnelPerDay": (partNoData?.recentActual?.personnelPerDay ?? 0).toFixed(1),
    // 生産能力（想定）
    "theoretical.equipmentCount": String(partNoData?.theoretical?.equipmentCount ?? 0),
    "theoretical.yield": (partNoData?.theoretical?.yield ?? 0).toFixed(2),
    "theoretical.nonWorkingTime": (partNoData?.theoretical?.nonWorkingTime ?? 0).toFixed(2),
    "theoretical.cycleTime": (partNoData?.theoretical?.cycleTime ?? 0).toFixed(1),
    "theoretical.personnelPerDay": (partNoData?.theoretical?.personnelPerDay ?? 0).toFixed(1),
    // 生産能力（目論見）- partNoDataがない場合はデフォルト値を使用
    "investmentPlan.equipmentCount": String(partNoData?.investmentPlan?.equipmentCount ?? 0),
    "investmentPlan.yield": (partNoData?.investmentPlan?.yield ?? defaultYield).toFixed(2),
    "investmentPlan.nonWorkingTime": (partNoData?.investmentPlan?.nonWorkingTime ?? 0).toFixed(2),
    "investmentPlan.cycleTime": (partNoData?.investmentPlan?.cycleTime ?? defaultCycleTime).toFixed(1),
    "investmentPlan.personnelPerDay": (partNoData?.investmentPlan?.personnelPerDay ?? 0).toFixed(1),
  });

  /**
   * 入力フィールドのデフォルト値を生成
   * @param data RationalizationSummaryInputData または null
   * @param partNoData 選択されたPartNoのデータ または null
   * @returns 入力フィールドの初期値オブジェクト
   */
  const getDefaultInputValues = (
    data: RationalizationSummaryInputData | null,
    partNoData: RationalizationProductionCapacityDataByPartNo | null
  ): { [key: string]: string } => ({
    // 基本情報
    "operatingDays": String(data?.operatingDays ?? 0),
    "partsCount": String(data?.partsCount ?? 1),
    // PartNo関連
    ...getPartNoInputValues(partNoData),
  });

  /** 入力フィールドの文字列値（フォーマット制御用） */
  const [inputValues, setInputValues] = useState<{ [key: string]: string }>(
    getDefaultInputValues(null, null)
  );

  /** デフォルト値 */
  const DEFAULT_TIME = "00:00";


  /** エラー情報（デフォルト値） */
  const defaultErrors = {
    // 基本情報
    fixedAssetNumber: { isError: false, errorMessage: "" },
    operatingDays: { isError: false, errorMessage: "" },
    workTime: { isError: false, errorMessage: "" },
    shiftSystem: { isError: false, errorMessage: "" },
    // 生産能力
    partsCount: { isError: false, errorMessage: "" },
    productionCapacity: { isError: false, errorMessage: "" },
    // 分析
    rationalizationReason: { isError: false, errorMessage: "" },
    productionPerEquipment: { isError: false, errorMessage: "" },
    productionPerPerson: { isError: false, errorMessage: "" },
    nonOperationMeasures: { isError: false, errorMessage: "" },
    nonProcessingMeasures: { isError: false, errorMessage: "" },
    currentEquipmentHandling: { isError: false, errorMessage: "" },
    laborSavingPlan: { isError: false, errorMessage: "" },
    expectedEffect: { isError: false, errorMessage: "" },
    // 費用グループ
    costCurrency: { isError: false, errorMessage: "" },
  };

  /** PartNoごとの生産能力エラー情報の型 */
  type ProductionCapacityErrors = {
    // 直近3ヶ月平均実績
    recentActualEquipmentCount: { isError: boolean; errorMessage: string };
    recentActualYield: { isError: boolean; errorMessage: string };
    recentActualCycleTime: { isError: boolean; errorMessage: string };
    recentActualPersonnelPerDay: { isError: boolean; errorMessage: string };
    // 想定
    theoreticalEquipmentCount: { isError: boolean; errorMessage: string };
    theoreticalYield: { isError: boolean; errorMessage: string };
    theoreticalCycleTime: { isError: boolean; errorMessage: string };
    theoreticalPersonnelPerDay: { isError: boolean; errorMessage: string };
    // 目論見
    investmentPlanEquipmentCount: { isError: boolean; errorMessage: string };
    investmentPlanYield: { isError: boolean; errorMessage: string };
    investmentPlanCycleTime: { isError: boolean; errorMessage: string };
    investmentPlanPersonnelPerDay: { isError: boolean; errorMessage: string };
  };

  /** デフォルトのPartNoエラー */
  const defaultPartNoErrors: ProductionCapacityErrors = {
    // 直近3ヶ月平均実績
    recentActualEquipmentCount: { isError: false, errorMessage: "" },
    recentActualYield: { isError: false, errorMessage: "" },
    recentActualCycleTime: { isError: false, errorMessage: "" },
    recentActualPersonnelPerDay: { isError: false, errorMessage: "" },
    // 想定
    theoreticalEquipmentCount: { isError: false, errorMessage: "" },
    theoreticalYield: { isError: false, errorMessage: "" },
    theoreticalCycleTime: { isError: false, errorMessage: "" },
    theoreticalPersonnelPerDay: { isError: false, errorMessage: "" },
    // 目論見
    investmentPlanEquipmentCount: { isError: false, errorMessage: "" },
    investmentPlanYield: { isError: false, errorMessage: "" },
    investmentPlanCycleTime: { isError: false, errorMessage: "" },
    investmentPlanPersonnelPerDay: { isError: false, errorMessage: "" },
  };

  /** エラー情報 */
  const [errors, setErrors] = useState<Errors>(JSON.parse(JSON.stringify(defaultErrors)));

  /** PartNoごとのエラー情報 */
  const [errorsByPartNo, setErrorsByPartNo] = useState<{ [partNo: string]: ProductionCapacityErrors }>({});


  /**
   * 初期化: 既存データがあればセット
   */
  useEffect(() => {
    if (rationalizationSummaryData) {
      let initialData = { ...rationalizationSummaryData };
      const originalSelectedPartNo = rationalizationSummaryData.selectedPartNo;

      // 全PartNoの計算値を算出
      const allPartNos = Object.keys(rationalizationSummaryData.productionCapacityByPartNo || {});
      allPartNos.forEach((partNo) => {
        initialData = { ...initialData, selectedPartNo: partNo };
        ["recentActual", "theoretical", "investmentPlan"].forEach((stateType) => {
          initialData = calculateRationalizationSummary(initialData, stateType as any);
        });
      });

      initialData = { ...initialData, selectedPartNo: originalSelectedPartNo };
      setLocalRationalizationSummary(initialData);

      const selectedPartNo = initialData.selectedPartNo;
      const partNoData = selectedPartNo
        ? initialData.productionCapacityByPartNo?.[selectedPartNo] ?? null
        : null;

      setInputValues(getDefaultInputValues(initialData, partNoData));

      // 指標グループと費用グループの初期化（費用は必須項目を補完）
      setIndicatorItems(ensureRequiredIndicatorItems(initialData.indicatorItems || []));
      setCostItems(ensureRequiredCostItems(initialData.costItems || []));
      // 費用の通貨IDの初期化（保存されている値があればそれを使用）
      if (initialData.costCurrencyId) {
        setSelectedCostCurrency(initialData.costCurrencyId);
      }
    }
  }, [rationalizationSummaryData]);

  /**
   * フィールド更新ハンドラー
   */
  const handleUpdate = (field: string, value: any) => {
    setLocalRationalizationSummary((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * 最大稼働日数（1ヶ月の最大日数）
   */
  const maxOperatingDays = 31;

  /**
   * 状態別データ更新ハンドラー（productionCapacityByPartNoのみ更新）
   */
  const handleStateUpdate = (
    stateType: "recentActual" | "theoretical" | "investmentPlan",
    field: keyof RationalizationProductionCapacityData,
    value: any
  ) => {
    setLocalRationalizationSummary((prev) => {
      const selectedPartNo = prev.selectedPartNo;
      if (!selectedPartNo) {
        return prev;
      }

      const currentData = prev.productionCapacityByPartNo?.[selectedPartNo] || {
        recentActual: { ...defaultProductionCapacityData },
        theoretical: { ...defaultProductionCapacityData },
        investmentPlan: { ...defaultProductionCapacityData },
      };

      const updatedState = {
        ...currentData[stateType],
        [field]: value,
      };

      const updated = {
        ...prev,
        productionCapacityByPartNo: {
          ...prev.productionCapacityByPartNo,
          [selectedPartNo]: {
            ...currentData,
            [stateType]: updatedState,
          },
        },
      };

      return calculateRationalizationSummary(updated, stateType);
    });
  };

  /**
   * 入力値変更ハンドラー（文字列をそのまま保持）
   */
  const handleInputChange = (
    stateType: "recentActual" | "theoretical" | "investmentPlan",
    field: keyof RationalizationProductionCapacityData,
    value: string
  ) => {
    const key = `${stateType}.${field}`;
    setInputValues(prev => ({ ...prev, [key]: value }));
    handleStateUpdate(stateType, field, Number(value) || 0);
  };

  /**
   * フォーカスが外れた時にフォーマット
   */
  const handleInputBlur = (
    stateType: "recentActual" | "theoretical" | "investmentPlan",
    field: keyof RationalizationProductionCapacityData
  ) => {
    const key = `${stateType}.${field}`;
    const currentValue = inputValues[key];
    const parsedValue = Number(currentValue);
    let max = 100000;
    if (field === "yield") max = 100;
    else if (field === "nonWorkingTime") max = 24;
    else if (field === "equipmentCount") max = 9999;
    const clampedValue = Math.min(Math.max(parsedValue || 0, 0), max);
    const decimals = (field === "cycleTime" || field === "personnelPerDay") ? 1 : (field === "equipmentCount" ? 0 : 2);
    const formattedValue = decimals === 0 ? String(Math.round(clampedValue)) : clampedValue.toFixed(decimals);
    setInputValues(prev => ({ ...prev, [key]: formattedValue }));
    handleStateUpdate(stateType, field, clampedValue);
  };

  /**
   * 稼働日数入力値変更ハンドラー
   */
  const handleOperatingDaysInputChange = (value: string) => {
    setInputValues(prev => ({ ...prev, "operatingDays": value }));
    const numericValue = value === "" ? 0 : Number(value);
    const safeValue = isNaN(numericValue) ? 0 : numericValue;
    handleUpdate("operatingDays", safeValue);
    ["recentActual", "theoretical", "investmentPlan"].forEach((stateType) => {
      setLocalRationalizationSummary((prev) =>
        calculateRationalizationSummary({ ...prev, operatingDays: safeValue }, stateType as any)
      );
    });
  };

  /**
   * 稼働日数フォーカスが外れた時にフォーマット
   */
  const handleOperatingDaysInputBlur = () => {
    const currentValue = inputValues["operatingDays"];
    const parsedValue = Number(currentValue);
    const clampedValue = Math.min(Math.max(parsedValue || 0, 0), maxOperatingDays);
    const formattedValue = String(Math.round(clampedValue));
    setInputValues(prev => ({ ...prev, "operatingDays": formattedValue }));
    handleUpdate("operatingDays", clampedValue);
    ["recentActual", "theoretical", "investmentPlan"].forEach((stateType) => {
      setLocalRationalizationSummary((prev) =>
        calculateRationalizationSummary({ ...prev, operatingDays: clampedValue }, stateType as any)
      );
    });
  };

  /**
   * 部品数入力値変更ハンドラー
   */
  const handlePartsCountInputChange = (value: string) => {
    setInputValues(prev => ({ ...prev, "partsCount": value }));
    const numericValue = value === "" ? 1 : Number(value);
    const safeValue = isNaN(numericValue) ? 1 : Math.max(numericValue, 1);
    handleUpdate("partsCount", safeValue);
  };

  /**
   * 部品数フォーカスが外れた時にフォーマット
   */
  const handlePartsCountInputBlur = () => {
    const currentValue = inputValues["partsCount"];
    const parsedValue = Number(currentValue);
    const clampedValue = Math.max(parsedValue || 1, 1);
    const formattedValue = String(Math.round(clampedValue));
    setInputValues(prev => ({ ...prev, "partsCount": formattedValue }));
    handleUpdate("partsCount", clampedValue);
  };

  /**
   * テキストフィールド更新ハンドラー
   */
  const handleTextFieldUpdate = (field: keyof RationalizationSummaryInputData, value: string) => {
    setLocalRationalizationSummary((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * 合理化前後サマリー計算
   */
  const calculateRationalizationSummary = (
    summaryData: RationalizationSummaryInputData,
    stateType: "recentActual" | "theoretical" | "investmentPlan"
  ): RationalizationSummaryInputData => {
    const selectedPartNo = summaryData.selectedPartNo;
    if (!selectedPartNo) return summaryData;

    const partNoData = summaryData.productionCapacityByPartNo?.[selectedPartNo];
    if (!partNoData) return summaryData;

    const state = partNoData[stateType];
    if (!state) return summaryData;

    const { equipmentCount, yield: yieldRate, nonWorkingTime, cycleTime, personnelPerDay } = state;
    const { operatingDays } = summaryData;
    const workTime = hourMinutesStringToMinutes(summaryData.workTime) / 60;
    const shiftSystem = Number(summaryData.shiftSystem);

    // 稼働率 = (24 - 非稼働時間) / 24 × 100
    const operatingRate = ((24 - (nonWorkingTime ?? 0)) / 24) * 100;

    // 生産能力 = 設備台数 × (稼働日数 × 24 × 3600) / サイクルタイム × 稼働率 × 歩留
    let productionVolume = 0;
    if (cycleTime && cycleTime > 0 && yieldRate && operatingDays && equipmentCount && equipmentCount > 0 && operatingRate) {
      productionVolume = Math.round(
        equipmentCount * ((operatingDays * 24 * 3600) / cycleTime) *
        (operatingRate / 100) *
        (yieldRate / 100)
      );
    }

    // 設備あたり生産性 = 生産能力 / 設備台数 / 稼働日数 / 24
    let productionPerEquipmentCalc = 0;
    if (productionVolume > 0 && operatingDays && operatingDays > 0 && equipmentCount && equipmentCount > 0) {
      productionPerEquipmentCalc = Math.round((productionVolume / equipmentCount / operatingDays / 24) * 100) / 100;
    }

    // 1人あたり生産性 = （生産能力 / 稼働日数 / （シフト数 × 労働時間）） / （シフト数 × 人数）
    let productionPerPersonCalc = 0;
    if (productionVolume > 0 && operatingDays && operatingDays > 0 && shiftSystem > 0 && workTime > 0 && (personnelPerDay ?? 0) > 0) {
      productionPerPersonCalc = Math.round(
        ((productionVolume / operatingDays / (shiftSystem * workTime)) /
          (shiftSystem * (personnelPerDay ?? 0))) * 100
      ) / 100;
    }

    return {
      ...summaryData,
      productionCapacityByPartNo: {
        ...summaryData.productionCapacityByPartNo,
        [selectedPartNo]: {
          ...partNoData,
          [stateType]: {
            ...state,
            productionVolume,
            productionPerEquipment: productionPerEquipmentCalc,
            productionPerPerson: productionPerPersonCalc,
          },
        },
      },
    };
  };

  /**
   * 一時保存ハンドラー
   */
  const handleTempSave = () => {
    const nameId = (itemNo ?? "unknown").replace(/[\\/:*?"<>|]/g, "_");
    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const snapshot = { localRationalizationSummary, indicatorItems, costItems, selectedCostCurrency, inputValues };
    const json = JSON.stringify({ screenName: "RationalizationSummaryInputPopover", nameId, savedAt: new Date().toISOString(), data: snapshot }, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `RationalizationSummaryInputPopover_${nameId}_${dateStr}.data`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: TITLE, description: t('components.RationalizationSummaryInputPopover.message.tempSaved'), status: "success", duration: 3000, isClosable: true });
  };

  /**
   * 一時保存読み込みハンドラー
   */
  const handleTempLoad = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".data";
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const parsed = JSON.parse(ev.target?.result as string);
          if (parsed.screenName !== "RationalizationSummaryInputPopover") {
            toast({ title: TITLE, description: t('components.RationalizationSummaryInputPopover.message.tempLoadScreenMismatch'), status: "error", duration: 4000, isClosable: true });
            return;
          }
          const { localRationalizationSummary: savedSummary, indicatorItems: savedIndicator, costItems: savedCost, selectedCostCurrency: savedCurrency, inputValues: savedInputValues } = parsed.data;
          setLocalRationalizationSummary(savedSummary);
          setIndicatorItems(savedIndicator);
          setCostItems(savedCost);
          setSelectedCostCurrency(savedCurrency);
          setInputValues(savedInputValues);
          toast({ title: TITLE, description: t('components.RationalizationSummaryInputPopover.message.tempLoaded'), status: "success", duration: 3000, isClosable: true });
        } catch {
          toast({ title: TITLE, description: t('components.RationalizationSummaryInputPopover.message.tempLoadFailed'), status: "error", duration: 4000, isClosable: true });
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  /**
   * 保存ハンドラー
   */
  const handleSave = async () => {
    // indicatorItems と costItems と costCurrencyId を含めて保存
    const dataToSave: RationalizationSummaryInputData = {
      ...localRationalizationSummary,
      processId: processId,
      itemNo: itemNo,
      indicatorItems: indicatorItems,
      costItems: costItems,
      costCurrencyId: selectedCostCurrency,
    };
    const result = await onSave(dataToSave);
    if (result) {
      setIsOpen(false);
    }
  }

  /**
   * リセットハンドラー
   */
  const handleReset = () => {
    const sourceData = rationalizationSummaryData ?? defaultRationalizationSummary;
    let data = { ...sourceData };
    const originalSelectedPartNo = sourceData.selectedPartNo;

    const allPartNos = Object.keys(sourceData.productionCapacityByPartNo || {});
    allPartNos.forEach((partNo) => {
      data = { ...data, selectedPartNo: partNo };
      ["recentActual", "theoretical", "investmentPlan"].forEach((stateType) => {
        data = calculateRationalizationSummary(data, stateType as any);
      });
    });

    data = { ...data, selectedPartNo: originalSelectedPartNo };
    setLocalRationalizationSummary(data);

    const selectedPartNo = data.selectedPartNo;
    const partNoData = selectedPartNo
      ? data.productionCapacityByPartNo?.[selectedPartNo] ?? null
      : null;

    setInputValues(getDefaultInputValues(data, partNoData));
    setErrors(JSON.parse(JSON.stringify(defaultErrors)));
    setErrorsByPartNo({});
    // 指標グループと費用グループのリセット
    setIndicatorItems(ensureRequiredIndicatorItems(sourceData.indicatorItems || []));
    setCostItems(ensureRequiredCostItems(sourceData.costItems || []));
    setSelectedIndicatorItemType("");
    setSelectedCostItemType("");
    setIsOpen(false);
  };

  /**
   * 数値フォーマット（ローカル）
   */
  const formatLocalNumber = (value: number | undefined): string => {
    if (value === undefined || value === null) return "";
    return value.toLocaleString();
  };

  const changeErrors = (name: string, errorMessage: string) => {
    setErrors((prevState) => ({
      ...prevState,
      [name]: { isError: true, errorMessage: `${errorMessage}` },
    }));
  };

  /**
   * バリデーション
   */
  const validate = () => {
    const required = (value: unknown) => {
      if (value === null || value === undefined) return false;
      const str = String(value).trim();
      return str !== "" && str !== "0" && str !== "０";
    };

    setErrors(JSON.parse(JSON.stringify(defaultErrors)));

    let isError = false;

    if (!required(localRationalizationSummary?.fixedAssetNumber ?? "")) {
      changeErrors("fixedAssetNumber", t('components.RationalizationSummaryInputPopover.validation.fixedAssetNumberRequired'));
      isError = true;
    }

    if (!required(String(localRationalizationSummary.operatingDays) ?? "")) {
      changeErrors("operatingDays", t('components.RationalizationSummaryInputPopover.validation.operatingDaysRequired'));
      isError = true;
    }

    if (!required(localRationalizationSummary.partsCount ?? "")) {
      changeErrors("partsCount", t('components.RationalizationSummaryInputPopover.validation.partsCountRequired'));
      isError = true;
    }

    // PartNoごとの生産能力チェック
    const missingPartNos: string[] = [];
    const newErrorsByPartNo: { [partNo: string]: ProductionCapacityErrors } = {};

    // バリデーション対象フィールドの定義
    const vPfx = 'components.RationalizationSummaryInputPopover.validation';
    const validationFields: {
      stateType: "recentActual" | "theoretical" | "investmentPlan";
      statePrefix: "recentActual" | "theoretical" | "investmentPlan";
      fields: { field: keyof RationalizationProductionCapacityData; errorKey: keyof ProductionCapacityErrors; errorMsgKey: string }[];
    }[] = [
        {
          stateType: "recentActual",
          statePrefix: "recentActual",
          fields: [
            { field: "equipmentCount", errorKey: "recentActualEquipmentCount", errorMsgKey: `${vPfx}.equipmentCountRequired` },
            { field: "yield", errorKey: "recentActualYield", errorMsgKey: `${vPfx}.yieldRequired` },
            { field: "cycleTime", errorKey: "recentActualCycleTime", errorMsgKey: `${vPfx}.cycleTimeRequired` },
            { field: "personnelPerDay", errorKey: "recentActualPersonnelPerDay", errorMsgKey: `${vPfx}.personnelPerDayRequired` },
          ],
        },
        {
          stateType: "theoretical",
          statePrefix: "theoretical",
          fields: [
            { field: "equipmentCount", errorKey: "theoreticalEquipmentCount", errorMsgKey: `${vPfx}.equipmentCountRequired` },
            { field: "yield", errorKey: "theoreticalYield", errorMsgKey: `${vPfx}.yieldRequired` },
            { field: "cycleTime", errorKey: "theoreticalCycleTime", errorMsgKey: `${vPfx}.cycleTimeRequired` },
            { field: "personnelPerDay", errorKey: "theoreticalPersonnelPerDay", errorMsgKey: `${vPfx}.personnelPerDayRequired` },
          ],
        },
        {
          stateType: "investmentPlan",
          statePrefix: "investmentPlan",
          fields: [
            { field: "equipmentCount", errorKey: "investmentPlanEquipmentCount", errorMsgKey: `${vPfx}.equipmentCountRequired` },
            { field: "yield", errorKey: "investmentPlanYield", errorMsgKey: `${vPfx}.yieldRequired` },
            { field: "cycleTime", errorKey: "investmentPlanCycleTime", errorMsgKey: `${vPfx}.cycleTimeRequired` },
            { field: "personnelPerDay", errorKey: "investmentPlanPersonnelPerDay", errorMsgKey: `${vPfx}.personnelPerDayRequired` },
          ],
        },
      ];

    if (partNoList.length > 0) {
      for (const partNoItem of partNoList) {
        const partNo = partNoItem.partNo;
        const partNoData = localRationalizationSummary.productionCapacityByPartNo?.[partNo];
        const partNoErrors: ProductionCapacityErrors = JSON.parse(JSON.stringify(defaultPartNoErrors));
        let hasError = false;

        // 各stateType（recentActual, theoretical, investmentPlan）のフィールドをチェック
        for (const { stateType, fields } of validationFields) {
          const stateData = partNoData?.[stateType];
          for (const { field, errorKey, errorMsgKey } of fields) {
            if (!required(stateData?.[field] ?? "")) {
              partNoErrors[errorKey] = { isError: true, errorMessage: t(errorMsgKey) };
              hasError = true;
            }
          }
        }

        if (hasError) {
          missingPartNos.push(partNo);
        }

        newErrorsByPartNo[partNo] = partNoErrors;
        if (hasError) {
          isError = true;
        }
      }

      setErrorsByPartNo(newErrorsByPartNo);

      if (missingPartNos.length > 0) {
        changeErrors("productionCapacity", t('components.RationalizationSummaryInputPopover.validation.partNoMissingInput', { partNos: missingPartNos.join(", ") }));
      }
    } else {
      setErrorsByPartNo({});
    }

    if (!required(localRationalizationSummary.rationalizationReason ?? "")) {
      changeErrors("rationalizationReason", t('components.RationalizationSummaryInputPopover.validation.rationalizationReasonRequired'));
      isError = true;
    }

    if (!required(localRationalizationSummary.nonOperationMeasures ?? "")) {
      changeErrors("nonOperationMeasures", t('components.RationalizationSummaryInputPopover.validation.nonOperationMeasuresRequired'));
      isError = true;
    }

    if (!required(localRationalizationSummary.productionPerEquipment ?? "")) {
      changeErrors("productionPerEquipment", t('components.RationalizationSummaryInputPopover.validation.productionPerEquipmentRequired'));
      isError = true;
    }

    if (!required(localRationalizationSummary.nonProcessingMeasures ?? "")) {
      changeErrors("nonProcessingMeasures", t('components.RationalizationSummaryInputPopover.validation.nonProcessingMeasuresRequired'));
      isError = true;
    }

    if (!required(localRationalizationSummary.productionPerPerson ?? "")) {
      changeErrors("productionPerPerson", t('components.RationalizationSummaryInputPopover.validation.productionPerPersonRequired'));
      isError = true;
    }

    if (!required(localRationalizationSummary.currentEquipmentHandling ?? "")) {
      changeErrors("currentEquipmentHandling", t('components.RationalizationSummaryInputPopover.validation.currentEquipmentHandlingRequired'));
      isError = true;
    }

    if (!required(localRationalizationSummary.laborSavingPlan ?? "")) {
      changeErrors("laborSavingPlan", t('components.RationalizationSummaryInputPopover.validation.laborSavingPlanRequired'));
      isError = true;
    }

    if (!required(localRationalizationSummary.expectedEffect ?? "")) {
      changeErrors("expectedEffect", t('components.RationalizationSummaryInputPopover.validation.expectedEffectRequired'));
      isError = true;
    }

    if (!required(localRationalizationSummary?.workTime === DEFAULT_TIME ? "" : (localRationalizationSummary?.workTime ?? ""))) {
      changeErrors("workTime", t('components.RationalizationSummaryInputPopover.validation.workTimeRequired'));
      isError = true;
    }

    if (!required(localRationalizationSummary?.shiftSystem ?? "")) {
      changeErrors("shiftSystem", t('components.RationalizationSummaryInputPopover.validation.shiftSystemRequired'));
      isError = true;
    }

    // 費用項目が1つ以上ある場合は通貨選択必須
    if (costItems.length > 0 && !selectedCostCurrency) {
      changeErrors("costCurrency", t('components.RationalizationSummaryInputPopover.validation.costCurrencyRequired'));
      isError = true;
    }

    return !isError;
  }

  const safeNumber = (value: any, defaultValue = 0): number => {
    const num = Number(value);
    if (value == null || !Number.isFinite(num)) {
      return defaultValue;
    }
    return num;
  };

  /**
   * 現在の工程に紐づくPartNoの一覧を取得
   */
  const partNoList = useMemo((): CycleTimeInSecondByPartNoType[] => {
    if (!processId || !processCodeData) return [];

    const selectedProcess = processCodeData.find(
      (p) => p.uniqueProcessId === processId
    );

    if (!selectedProcess?.uniqueProcessCycleTimeInSecondByPartNo) return [];

    try {
      const parsed = JSON.parse(selectedProcess.uniqueProcessCycleTimeInSecondByPartNo);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [processId, processCodeData]);

  /**
   * 選択中のPartNoのエラー情報を取得
   */
  const currentPartNoErrors = useMemo((): ProductionCapacityErrors => {
    const selectedPartNo = localRationalizationSummary.selectedPartNo;
    if (!selectedPartNo) return defaultPartNoErrors;
    return errorsByPartNo[selectedPartNo] || defaultPartNoErrors;
  }, [localRationalizationSummary.selectedPartNo, errorsByPartNo]);

  /**
   * PartNo選択変更ハンドラー
   */
  const handlePartNoChange = (newPartNo: string) => {
    setLocalRationalizationSummary((prev) => {
      let updated = { ...prev };

      let newPartNoData = prev.productionCapacityByPartNo?.[newPartNo];
      if (!newPartNoData) {
        newPartNoData = {
          recentActual: { ...defaultProductionCapacityData },
          theoretical: { ...defaultProductionCapacityData },
          investmentPlan: {
            ...defaultProductionCapacityData,
            yield: productionLineData?.yield || 0,
            cycleTime: productionLineData?.cycleTime || undefined,
          },
        };
        updated.productionCapacityByPartNo = {
          ...prev.productionCapacityByPartNo,
          [newPartNo]: newPartNoData,
        };
      }

      updated.selectedPartNo = newPartNo;

      ["recentActual", "theoretical", "investmentPlan"].forEach((stateType) => {
        updated = calculateRationalizationSummary(updated, stateType as any);
      });

      return updated;
    });

    if (newPartNo) {
      const partNoData = localRationalizationSummary.productionCapacityByPartNo?.[newPartNo] ?? null;
      const defaultYield = productionLineData?.yield || 0;
      const defaultCycleTime = productionLineData?.cycleTime || 0;
      setInputValues(prev => ({
        ...prev,
        ...getPartNoInputValues(partNoData, defaultYield, defaultCycleTime),
      }));
    }
  };

  useEffect(() => {
    setLocalRationalizationSummary((prev) => {
      let updated = { ...prev };
      const originalSelectedPartNo = prev.selectedPartNo;

      const allPartNos = Object.keys(prev.productionCapacityByPartNo || {});
      allPartNos.forEach((partNo) => {
        updated = { ...updated, selectedPartNo: partNo };
        ["recentActual", "theoretical", "investmentPlan"].forEach((stateType) => {
          updated = calculateRationalizationSummary(updated, stateType as any);
        });
      });

      return { ...updated, selectedPartNo: originalSelectedPartNo };
    });
  }, [localRationalizationSummary.workTime, localRationalizationSummary.shiftSystem, localRationalizationSummary.operatingDays]);

  /**
   * 累積列幅を返す
   * @param columnPos 列番号(1オリジン)
   * @returns 累積列幅
   */
  const getFixedColumnLeft = (columnPos: number) => {
    let width: number = 0;

    if (1 < columnPos) width += WIDTH_ITEM_NAME;
    if (2 < columnPos) width += WIDTH_OPERATE;
    if (3 < columnPos) width += WIDTH_KBN;

    return width;
  }

  /**
   * レンダリング
   */
  return (
    <>
      <Button
        size="sm"
        onClick={() => setIsOpen(true)}
        // isDisabled={!pagePermissions?.isEditable}
        m={1}
      >
        {t('components.RationalizationSummaryInputPopover.button.summaryInput')}
      </Button>

      <Modal isOpen={isOpen} onClose={handleReset} closeOnOverlayClick={false} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent minW={1500}>
          <ModalHeader>
            <HStack spacing={1} align="center">
              <Text as="span">{TITLE}</Text>
              <Popover placement="auto">
                <PopoverTrigger>
                  <Button
                    style={{ background: 'none', border: 'none', padding: 0 }}
                    minW="auto"
                    h="auto"
                    ml={1}
                    mt={1}
                  >
                    <FaRegQuestionCircle size="16px" cursor="pointer" />
                  </Button>
                </PopoverTrigger>
                <Portal>
                  <PopoverContent width="fit-content" maxW="750px" bgColor="#fffff0">
                    <PopoverArrow />
                    <PopoverBody>
                      <Text fontSize="sm" whiteSpace="pre-wrap">{t('components.RationalizationSummaryInputPopover.label.headerTooltip')}</Text>
                    </PopoverBody>
                  </PopoverContent>
                </Portal>
              </Popover>
            </HStack>
          </ModalHeader>
          <ModalCloseButton isDisabled={isLoading} />
          <ModalBody>
            {/* NumberIncrementStepper/NumberDecrementStepperはネイティブbuttonではないためfieldsetのdisabledが効かず、pointerEvents:'none'で明示的にクリック不可にする */}
            <fieldset disabled={!pagePermissions?.isEditable} style={!pagePermissions?.isEditable ? { pointerEvents: 'none' } : undefined}>
              <VStack align="stretch" spacing={4}>
                {/* ProcessCode / ItemNo */}
                <FormControl mb={2}>
                  <HStack spacing={2} alignItems="center">
                    <Text textAlign="left" fontWeight="semibold">ProcessCode:</Text>
                    <Text>{processId}</Text>
                  </HStack>
                  <HStack spacing={2} alignItems="center">
                    <Text textAlign="left" fontWeight="semibold">ItemNo:</Text>
                    <Text>{itemNo}</Text>
                  </HStack>
                </FormControl>

                {/* 基本情報 */}
                <Box>
                  <Text fontWeight="bold" mb={1}>{t('components.RationalizationSummaryInputPopover.section.basicInfo')}</Text>
                  <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={4} fontSize="sm">
                    <VStack align="stretch" spacing={2}>
                      <HStack spacing={4} alignItems="flex-start">
                        <FormControl width="50%" isInvalid={!!errors.fixedAssetNumber.isError}>
                          <FormLabel fontSize="sm">{t('components.RationalizationSummaryInputPopover.label.fixedAssetNumber')}</FormLabel>
                          <Input
                            fontSize="sm"
                            value={localRationalizationSummary.fixedAssetNumber || ""}
                            onChange={(e) => handleUpdate("fixedAssetNumber", e.target.value)}
                          />
                          <FormErrorMessage>{errors.fixedAssetNumber.errorMessage}</FormErrorMessage>
                        </FormControl>
                      </HStack>
                      <HStack spacing={4} alignItems="flex-start">
                        <FormControl w="150px" isInvalid={!!errors.operatingDays.isError}>
                          <FormLabel fontSize="sm">{t('components.RationalizationSummaryInputPopover.label.operatingDays')}</FormLabel>
                          <NumberInput
                            fontSize="sm"
                            value={inputValues["operatingDays"]}
                            onChange={(value) => handleOperatingDaysInputChange(value)}
                            onBlur={() => handleOperatingDaysInputBlur()}
                            min={0}
                            max={maxOperatingDays}
                          >
                            <NumberInputField placeholder={t('components.RationalizationSummaryInputPopover.placeholder.day')} />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                          <FormErrorMessage>{errors.operatingDays.errorMessage}</FormErrorMessage>
                        </FormControl>
                        <FormControl w="120px" isInvalid={!!errors.workTime.isError}>
                          <FormLabel fontSize="sm">{t('components.RationalizationSummaryInputPopover.label.workTime')}</FormLabel>
                          <CeasTimePicker
                            isInvalid={!!errors.workTime.isError}
                            value={localRationalizationSummary.workTime || DEFAULT_TIME}
                            onChange={(value) => handleUpdate("workTime", value)}
                          />
                          <FormErrorMessage>{errors.workTime.errorMessage}</FormErrorMessage>
                        </FormControl>
                        <FormControl w="120px" isInvalid={!!errors.shiftSystem.isError}>
                          <FormLabel fontSize="sm">{t('components.RationalizationSummaryInputPopover.label.shiftSystem')}</FormLabel>
                          <Select
                            fontSize="sm"
                            value={localRationalizationSummary.shiftSystem || ShiftSystem.OneShiftSystem.code}
                            onChange={(e) => handleUpdate("shiftSystem", e.target.value)}
                          >
                            {Object.entries(codeToNameMap(ShiftSystem)).map(([code, name]) => (
                              <option key={code} value={code}>{name}</option>
                            ))}
                          </Select>
                          <FormErrorMessage>{errors.shiftSystem.errorMessage}</FormErrorMessage>
                        </FormControl>
                      </HStack>
                      <Text fontSize="xs" color="gray.600">
                        {t('components.RationalizationSummaryInputPopover.help.recentActualNote')}
                      </Text>
                      <HStack spacing={4} alignItems="flex-start">
                        <FormControl w="180px" isInvalid={!!errors.partsCount.isError}>
                          <FormLabel fontSize="sm">{t('components.RationalizationSummaryInputPopover.label.partsCount')}</FormLabel>
                          <HStack spacing={1}>
                            <NumberInput
                              size="sm"
                              width="120px"
                              min={1}
                              value={inputValues["partsCount"]}
                              onChange={(value) => handlePartsCountInputChange(value)}
                              onBlur={() => handlePartsCountInputBlur()}
                            >
                              <NumberInputField placeholder={t('components.RationalizationSummaryInputPopover.placeholder.pcs')} textAlign="right" />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                            <Text fontSize="xs">pcs</Text>
                          </HStack>
                          <FormErrorMessage>{errors.partsCount.errorMessage}</FormErrorMessage>
                        </FormControl>
                      </HStack>
                    </VStack>
                  </Box>
                </Box>
              </VStack>
            </fieldset>

            {/* 合理化効果比較（PartNoは複数パーツのデータ参照のため常に活性化） */}
            <Box>
              <Text fontWeight="bold" mb={3}>{t('components.RationalizationSummaryInputPopover.section.rationalizationComparison')}</Text>
              <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={4} fontSize="sm">

                <HStack spacing={2} mb={3} justifyContent="flex-start">
                  <Text fontSize="sm" fontWeight="medium">PartNo</Text>
                  <Select
                    size="sm"
                    w="200px"
                    value={localRationalizationSummary.selectedPartNo || ""}
                    onChange={(e) => handlePartNoChange(e.target.value)}
                  >
                    <option value="">{t('components.RationalizationSummaryInputPopover.option.selectPlaceholder')}</option>
                    {partNoList.map((item) => (
                      <option key={item.partNo} value={item.partNo}>{resolveDisplayPartNoByKey(item.partNo, partNoData)}</option>
                    ))}
                  </Select>
                </HStack>
                <fieldset disabled={!pagePermissions?.isEditable} style={!pagePermissions?.isEditable ? { pointerEvents: 'none' } : undefined}>
                  {errors.productionCapacity?.isError && (
                    <Text color="red.500" fontSize="sm" mb={3}>{errors.productionCapacity.errorMessage}</Text>
                  )}
                  {localRationalizationSummary.selectedPartNo && (
                    <Table variant="simple" size="sm" border="1px solid" borderColor="gray.200" sx={{
                      tableLayout: "fixed",
                      borderCollapse: "collapse",
                      "& td, & th": { borderBottom: "1px solid", borderBottomColor: "gray.200" }
                    }}>
                      <colgroup>
                        <col style={{ width: "180px" }} />
                        <col style={{ width: "150px" }} />
                        <col style={{ width: "150px" }} />
                        <col style={{ width: "150px" }} />
                        <col style={{ width: "150px" }} />
                      </colgroup>
                      <Thead>
                        <Tr>
                          <Th rowSpan={2} borderRight="1px solid" borderColor="gray.200">{t('components.RationalizationSummaryInputPopover.label.item')}</Th>
                          <Th colSpan={2} textAlign="center" borderRight="1px solid" borderColor="gray.200" borderBottom="1px solid" borderBottomColor="gray.200">{t('components.RationalizationSummaryInputPopover.label.current')}</Th>
                          <Th colSpan={1} textAlign="center" borderRight="1px solid" borderColor="gray.200" borderBottom="1px solid" borderBottomColor="gray.200">{t('components.RationalizationSummaryInputPopover.label.rationalization')}</Th>
                          <Th colSpan={1} textAlign="center" borderRight="1px solid" borderColor="gray.200" borderBottom="1px solid" borderBottomColor="gray.200">{t('components.RationalizationSummaryInputPopover.label.diff')}</Th>
                        </Tr>
                        <Tr>
                          <Th borderRight="1px solid" borderColor="gray.200" textAlign="center">{t('components.RationalizationSummaryInputPopover.label.recentActual')}</Th>
                          <Th borderRight="1px solid" borderColor="gray.200" textAlign="center">{t('components.RationalizationSummaryInputPopover.label.theoretical')}</Th>
                          <Th borderRight="1px solid" borderColor="gray.200" textAlign="center">{t('components.RationalizationSummaryInputPopover.label.investmentPlan')}</Th>
                          <Th borderRight="1px solid" borderColor="gray.200" textAlign="center">{t('components.RationalizationSummaryInputPopover.label.actualVsPlan')}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {/* 設備台数 */}
                        <Tr h="48px">
                          <Td borderRight="1px solid" borderColor="gray.200">{t('components.RationalizationSummaryInputPopover.label.equipmentCount')}</Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            <Box display="flex" justifyContent="center">
                              <HStack spacing={1}>
                                <FormControl maxW="80px" isInvalid={!!currentPartNoErrors.recentActualEquipmentCount.isError}>
                                  <NumberInput size="sm" width="80px" min={0} max={9999}
                                    value={inputValues["recentActual.equipmentCount"]}
                                    onChange={(value) => handleInputChange("recentActual", "equipmentCount", value)}
                                    onBlur={() => handleInputBlur("recentActual", "equipmentCount")}>
                                    <NumberInputField textAlign="right" />
                                    <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                                  </NumberInput>
                                  <FormErrorMessage display="flex" justifyContent="center">{currentPartNoErrors.recentActualEquipmentCount.errorMessage}</FormErrorMessage>
                                </FormControl>
                                <Text w="20px" fontSize="xs">{t('components.RationalizationSummaryInputPopover.help.equipmentUnit')}</Text>
                              </HStack>
                            </Box>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            <Box display="flex" justifyContent="center">
                              <HStack spacing={1}>
                                <FormControl maxW="80px" isInvalid={!!currentPartNoErrors.theoreticalEquipmentCount.isError}>
                                  <NumberInput size="sm" width="80px" min={0} max={9999}
                                    value={inputValues["theoretical.equipmentCount"]}
                                    onChange={(value) => handleInputChange("theoretical", "equipmentCount", value)}
                                    onBlur={() => handleInputBlur("theoretical", "equipmentCount")}>
                                    <NumberInputField textAlign="right" />
                                    <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                                  </NumberInput>
                                  <FormErrorMessage display="flex" justifyContent="center">{currentPartNoErrors.theoreticalEquipmentCount.errorMessage}</FormErrorMessage>
                                </FormControl>
                                <Text w="20px" fontSize="xs">{t('components.RationalizationSummaryInputPopover.help.equipmentUnit')}</Text>
                              </HStack>
                            </Box>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            <Box display="flex" justifyContent="center">
                              <HStack spacing={1}>
                                <FormControl maxW="80px" isInvalid={!!currentPartNoErrors.investmentPlanEquipmentCount.isError}>
                                  <NumberInput size="sm" width="80px" min={0} max={9999}
                                    value={inputValues["investmentPlan.equipmentCount"]}
                                    onChange={(value) => handleInputChange("investmentPlan", "equipmentCount", value)}
                                    onBlur={() => handleInputBlur("investmentPlan", "equipmentCount")}>
                                    <NumberInputField textAlign="right" />
                                    <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                                  </NumberInput>
                                  <FormErrorMessage display="flex" justifyContent="center">{currentPartNoErrors.investmentPlanEquipmentCount.errorMessage}</FormErrorMessage>
                                </FormControl>
                                <Text w="20px" fontSize="xs">{t('components.RationalizationSummaryInputPopover.help.equipmentUnit')}</Text>
                              </HStack>
                            </Box>
                          </Td>
                          <Td textAlign="center">
                            {safeNumber((currentPartNoData.investmentPlan?.equipmentCount ?? 0) - (currentPartNoData.recentActual?.equipmentCount ?? 0)) + " 台"}
                          </Td>
                        </Tr>

                        {/* 歩留 */}
                        <Tr h="48px">
                          <Td borderRight="1px solid" borderColor="gray.200">{t('components.RationalizationSummaryInputPopover.label.yield')}</Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            <Box display="flex" justifyContent="center">
                              <HStack spacing={1}>
                                <FormControl maxW="80px" isInvalid={!!currentPartNoErrors.recentActualYield.isError}>
                                  <NumberInput size="sm" width="80px" min={0} max={100} precision={2} step={0.01}
                                    value={inputValues["recentActual.yield"]}
                                    onChange={(value) => handleInputChange("recentActual", "yield", value)}
                                    onBlur={() => handleInputBlur("recentActual", "yield")}>
                                    <NumberInputField textAlign="right" />
                                    <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                                  </NumberInput>
                                  <FormErrorMessage display="flex" justifyContent="center">{currentPartNoErrors.recentActualYield.errorMessage}</FormErrorMessage>
                                </FormControl>
                                <Text w="20px" fontSize="xs">%</Text>
                              </HStack>
                            </Box>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            <Box display="flex" justifyContent="center">
                              <HStack spacing={1}>
                                <FormControl maxW="80px" isInvalid={!!currentPartNoErrors.theoreticalYield.isError}>
                                  <NumberInput size="sm" width="80px" min={0} max={100} precision={2} step={0.01}
                                    value={inputValues["theoretical.yield"]}
                                    onChange={(value) => handleInputChange("theoretical", "yield", value)}
                                    onBlur={() => handleInputBlur("theoretical", "yield")}>
                                    <NumberInputField textAlign="right" />
                                    <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                                  </NumberInput>
                                  <FormErrorMessage display="flex" justifyContent="center">{currentPartNoErrors.theoreticalYield.errorMessage}</FormErrorMessage>
                                </FormControl>
                                <Text w="20px" fontSize="xs">%</Text>
                              </HStack>
                            </Box>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            <Box display="flex" justifyContent="center">
                              <HStack spacing={1}>
                                <FormControl maxW="80px" isInvalid={!!currentPartNoErrors.investmentPlanYield.isError}>
                                  <NumberInput size="sm" width="80px" min={0} max={100} precision={2} step={0.01}
                                    value={inputValues["investmentPlan.yield"]}
                                    onChange={(value) => handleInputChange("investmentPlan", "yield", value)}
                                    onBlur={() => handleInputBlur("investmentPlan", "yield")}>
                                    <NumberInputField textAlign="right" />
                                    <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                                  </NumberInput>
                                  <FormErrorMessage display="flex" justifyContent="center">{currentPartNoErrors.investmentPlanYield.errorMessage}</FormErrorMessage>
                                </FormControl>
                                <Text w="20px" fontSize="xs">%</Text>
                              </HStack>
                            </Box>
                          </Td>
                          <Td textAlign="center">
                            {safeNumber((currentPartNoData.investmentPlan?.yield ?? 0) - (currentPartNoData.recentActual?.yield ?? 0)).toFixed(2) + " %"}
                          </Td>
                        </Tr>

                        {/* 非稼働時間 */}
                        <Tr h="48px">
                          <Td borderRight="1px solid" borderColor="gray.200">{t('components.RationalizationSummaryInputPopover.label.nonWorkingTime')}</Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            <Box display="flex" justifyContent="center">
                              <HStack spacing={1}>
                                <FormControl maxW="80px">
                                  <NumberInput size="sm" width="80px" min={0} max={24} precision={2} step={0.1}
                                    value={inputValues["recentActual.nonWorkingTime"]}
                                    onChange={(value) => handleInputChange("recentActual", "nonWorkingTime", value)}
                                    onBlur={() => handleInputBlur("recentActual", "nonWorkingTime")}>
                                    <NumberInputField textAlign="right" placeholder="hr" />
                                    <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                                  </NumberInput>
                                </FormControl>
                                <Text w="20px" fontSize="xs">hr</Text>
                              </HStack>
                            </Box>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            <Box display="flex" justifyContent="center">
                              <HStack spacing={1}>
                                <FormControl maxW="80px">
                                  <NumberInput size="sm" width="80px" min={0} max={24} precision={2} step={0.1}
                                    value={inputValues["theoretical.nonWorkingTime"]}
                                    onChange={(value) => handleInputChange("theoretical", "nonWorkingTime", value)}
                                    onBlur={() => handleInputBlur("theoretical", "nonWorkingTime")}>
                                    <NumberInputField textAlign="right" placeholder="hr" />
                                    <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                                  </NumberInput>
                                </FormControl>
                                <Text w="20px" fontSize="xs">hr</Text>
                              </HStack>
                            </Box>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            <Box display="flex" justifyContent="center">
                              <HStack spacing={1}>
                                <FormControl maxW="80px">
                                  <NumberInput size="sm" width="80px" min={0} max={24} precision={2} step={0.1}
                                    value={inputValues["investmentPlan.nonWorkingTime"]}
                                    onChange={(value) => handleInputChange("investmentPlan", "nonWorkingTime", value)}
                                    onBlur={() => handleInputBlur("investmentPlan", "nonWorkingTime")}>
                                    <NumberInputField textAlign="right" placeholder="hr" />
                                    <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                                  </NumberInput>
                                </FormControl>
                                <Text w="20px" fontSize="xs">hr</Text>
                              </HStack>
                            </Box>
                          </Td>
                          <Td textAlign="center">
                            {safeNumber((currentPartNoData.investmentPlan?.nonWorkingTime ?? 0) - (currentPartNoData.recentActual?.nonWorkingTime ?? 0)).toFixed(2) + " hr"}
                          </Td>
                        </Tr>

                        {/* 稼働率 */}
                        <Tr bg="gray.50" h="48px">
                          <Td borderRight="1px solid" borderColor="gray.200">
                            <HStack spacing={1}>
                              <Text>{t('components.RationalizationSummaryInputPopover.label.operatingRate')}</Text>
                              <Popover placement="auto">
                                <PopoverTrigger>
                                  <Button style={{ background: 'none', border: 'none', padding: 0 }} minW="auto" h="auto" ml={1}>
                                    <FaRegQuestionCircle size="16px" cursor="pointer" />
                                  </Button>
                                </PopoverTrigger>
                                <Portal>
                                  <PopoverContent width="fit-content" bgColor="#fffff0">
                                    <PopoverArrow />
                                    <PopoverBody>
                                      <Text fontSize="sm">{t('components.RationalizationSummaryInputPopover.help.operatingRateFormula')}</Text>
                                    </PopoverBody>
                                  </PopoverContent>
                                </Portal>
                              </Popover>
                            </HStack>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200" textAlign="center" bg="gray.50">
                            {(((24 - (currentPartNoData.recentActual?.nonWorkingTime ?? 0)) / 24) * 100).toFixed(2) + " %"}
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200" textAlign="center" bg="gray.50">
                            {(((24 - (currentPartNoData.theoretical?.nonWorkingTime ?? 0)) / 24) * 100).toFixed(2) + " %"}
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200" textAlign="center" bg="gray.50">
                            {(((24 - (currentPartNoData.investmentPlan?.nonWorkingTime ?? 0)) / 24) * 100).toFixed(2) + " %"}
                          </Td>
                          <Td textAlign="center" bg="gray.50">
                            {((((24 - (currentPartNoData.investmentPlan?.nonWorkingTime ?? 0)) / 24) - ((24 - (currentPartNoData.recentActual?.nonWorkingTime ?? 0)) / 24)) * 100).toFixed(2) + " %"}
                          </Td>
                        </Tr>

                        {/* サイクルタイム */}
                        <Tr h="48px">
                          <Td borderRight="1px solid" borderColor="gray.200">{t('components.RationalizationSummaryInputPopover.label.cycleTime')}</Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            <Box display="flex" justifyContent="center">
                              <HStack spacing={1}>
                                <FormControl maxW="80px" isInvalid={!!currentPartNoErrors.recentActualCycleTime.isError}>
                                  <NumberInput size="sm" width="80px" min={0} max={9999} precision={1} step={0.1}
                                    value={inputValues["recentActual.cycleTime"]}
                                    onChange={(value) => handleInputChange("recentActual", "cycleTime", value)}
                                    onBlur={() => handleInputBlur("recentActual", "cycleTime")}>
                                    <NumberInputField textAlign="right" />
                                    <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                                  </NumberInput>
                                  <FormErrorMessage display="flex" justifyContent="center">{currentPartNoErrors.recentActualCycleTime.errorMessage}</FormErrorMessage>
                                </FormControl>
                                <Text w="20px" fontSize="xs">sec</Text>
                              </HStack>
                            </Box>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            <Box display="flex" justifyContent="center">
                              <HStack spacing={1}>
                                <FormControl maxW="80px" isInvalid={!!currentPartNoErrors.theoreticalCycleTime.isError}>
                                  <NumberInput size="sm" width="80px" min={0} max={9999} precision={1} step={0.1}
                                    value={inputValues["theoretical.cycleTime"]}
                                    onChange={(value) => handleInputChange("theoretical", "cycleTime", value)}
                                    onBlur={() => handleInputBlur("theoretical", "cycleTime")}>
                                    <NumberInputField textAlign="right" />
                                    <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                                  </NumberInput>
                                  <FormErrorMessage display="flex" justifyContent="center">{currentPartNoErrors.theoreticalCycleTime.errorMessage}</FormErrorMessage>
                                </FormControl>
                                <Text w="20px" fontSize="xs">sec</Text>
                              </HStack>
                            </Box>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            <Box display="flex" justifyContent="center">
                              <HStack spacing={1}>
                                <FormControl maxW="80px" isInvalid={!!currentPartNoErrors.investmentPlanCycleTime.isError}>
                                  <NumberInput size="sm" width="80px" min={0} max={9999} precision={1} step={0.1}
                                    value={inputValues["investmentPlan.cycleTime"]}
                                    onChange={(value) => handleInputChange("investmentPlan", "cycleTime", value)}
                                    onBlur={() => handleInputBlur("investmentPlan", "cycleTime")}>
                                    <NumberInputField textAlign="right" />
                                    <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                                  </NumberInput>
                                  <FormErrorMessage display="flex" justifyContent="center">{currentPartNoErrors.investmentPlanCycleTime.errorMessage}</FormErrorMessage>
                                </FormControl>
                                <Text w="20px" fontSize="xs">sec</Text>
                              </HStack>
                            </Box>
                          </Td>
                          <Td textAlign="center">
                            {safeNumber((currentPartNoData.investmentPlan?.cycleTime ?? 0) - (currentPartNoData.recentActual?.cycleTime ?? 0)).toFixed(1) + " sec"}
                          </Td>
                        </Tr>

                        {/* 1日あたり必要人員数 */}
                        <Tr h="48px">
                          <Td borderRight="1px solid" borderColor="gray.200">{t('components.RationalizationSummaryInputPopover.label.personnelPerDay')}</Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            <Box display="flex" justifyContent="center">
                              <HStack spacing={1}>
                                <FormControl maxW="80px" isInvalid={!!currentPartNoErrors.recentActualPersonnelPerDay.isError}>
                                  <NumberInput size="sm" width="80px" min={0} max={9999} precision={1} step={0.1}
                                    value={inputValues["recentActual.personnelPerDay"]}
                                    onChange={(value) => handleInputChange("recentActual", "personnelPerDay", value)}
                                    onBlur={() => handleInputBlur("recentActual", "personnelPerDay")}>
                                    <NumberInputField textAlign="right" />
                                    <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                                  </NumberInput>
                                  <FormErrorMessage display="flex" justifyContent="center">{currentPartNoErrors.recentActualPersonnelPerDay.errorMessage}</FormErrorMessage>
                                </FormControl>
                                <Text w="20px" fontSize="xs">pax</Text>
                              </HStack>
                            </Box>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            <Box display="flex" justifyContent="center">
                              <HStack spacing={1}>
                                <FormControl maxW="80px" isInvalid={!!currentPartNoErrors.theoreticalPersonnelPerDay.isError}>
                                  <NumberInput size="sm" width="80px" min={0} max={9999} precision={1} step={0.1}
                                    value={inputValues["theoretical.personnelPerDay"]}
                                    onChange={(value) => handleInputChange("theoretical", "personnelPerDay", value)}
                                    onBlur={() => handleInputBlur("theoretical", "personnelPerDay")}>
                                    <NumberInputField textAlign="right" />
                                    <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                                  </NumberInput>
                                  <FormErrorMessage display="flex" justifyContent="center">{currentPartNoErrors.theoreticalPersonnelPerDay.errorMessage}</FormErrorMessage>
                                </FormControl>
                                <Text w="20px" fontSize="xs">pax</Text>
                              </HStack>
                            </Box>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            <Box display="flex" justifyContent="center">
                              <HStack spacing={1}>
                                <FormControl maxW="80px" isInvalid={!!currentPartNoErrors.investmentPlanPersonnelPerDay.isError}>
                                  <NumberInput size="sm" width="80px" min={0} max={9999} precision={1} step={0.1}
                                    value={inputValues["investmentPlan.personnelPerDay"]}
                                    onChange={(value) => handleInputChange("investmentPlan", "personnelPerDay", value)}
                                    onBlur={() => handleInputBlur("investmentPlan", "personnelPerDay")}>
                                    <NumberInputField textAlign="right" />
                                    <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                                  </NumberInput>
                                  <FormErrorMessage display="flex" justifyContent="center">{currentPartNoErrors.investmentPlanPersonnelPerDay.errorMessage}</FormErrorMessage>
                                </FormControl>
                                <Text w="20px" fontSize="xs">pax</Text>
                              </HStack>
                            </Box>
                          </Td>
                          <Td textAlign="center">
                            {safeNumber((currentPartNoData.investmentPlan?.personnelPerDay ?? 0) - (currentPartNoData.recentActual?.personnelPerDay ?? 0)).toFixed(1) + " pax"}
                          </Td>
                        </Tr>

                        {/* 生産能力（計算値） */}
                        <Tr bg="gray.50" h="48px">
                          <Td borderRight="1px solid" borderColor="gray.200">
                            <HStack spacing={1}>
                              <Text>{t('components.RationalizationSummaryInputPopover.label.productionVolume')}</Text>
                              <Popover placement="auto">
                                <PopoverTrigger>
                                  <Button style={{ background: 'none', border: 'none', padding: 0 }} minW="auto" h="auto" ml={1}>
                                    <FaRegQuestionCircle size="16px" cursor="pointer" />
                                  </Button>
                                </PopoverTrigger>
                                <Portal>
                                  <PopoverContent width="fit-content" bgColor="#fffff0">
                                    <PopoverArrow />
                                    <PopoverBody>
                                      <Text fontSize="sm">{t('components.RationalizationSummaryInputPopover.help.productionVolumeFormula')}</Text>
                                    </PopoverBody>
                                  </PopoverContent>
                                </Portal>
                              </Popover>
                            </HStack>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                            {formatLocalNumber(currentPartNoData.recentActual?.productionVolume ?? 0) + " pcs"}
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                            {formatLocalNumber(currentPartNoData.theoretical?.productionVolume ?? 0) + " pcs"}
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                            {formatLocalNumber(currentPartNoData.investmentPlan?.productionVolume ?? 0) + " pcs"}
                          </Td>
                          <Td textAlign="center">
                            {formatLocalNumber(safeNumber((currentPartNoData.investmentPlan?.productionVolume ?? 0) - (currentPartNoData.recentActual?.productionVolume ?? 0))) + " pcs"}
                          </Td>
                        </Tr>

                        {/* 設備あたり生産性（計算値） */}
                        <Tr bg="gray.50" h="48px">
                          <Td borderRight="1px solid" borderColor="gray.200">
                            <HStack spacing={1}>
                              <Text>{t('components.RationalizationSummaryInputPopover.label.productionPerEquipment')}</Text>
                              <Popover placement="auto">
                                <PopoverTrigger>
                                  <Button style={{ background: 'none', border: 'none', padding: 0 }} minW="auto" h="auto" ml={1}>
                                    <FaRegQuestionCircle size="16px" cursor="pointer" />
                                  </Button>
                                </PopoverTrigger>
                                <Portal>
                                  <PopoverContent width="fit-content" bgColor="#fffff0">
                                    <PopoverArrow />
                                    <PopoverBody>
                                      <Text fontSize="sm">{t('components.RationalizationSummaryInputPopover.help.productionPerEquipmentFormula')}</Text>
                                    </PopoverBody>
                                  </PopoverContent>
                                </Portal>
                              </Popover>
                            </HStack>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                            {safeNumber(currentPartNoData.recentActual?.productionPerEquipment ?? 0).toFixed(2) + " pcs"}
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                            {safeNumber(currentPartNoData.theoretical?.productionPerEquipment ?? 0).toFixed(2) + " pcs"}
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                            {safeNumber(currentPartNoData.investmentPlan?.productionPerEquipment ?? 0).toFixed(2) + " pcs"}
                          </Td>
                          <Td textAlign="center">
                            {safeNumber((currentPartNoData.investmentPlan?.productionPerEquipment ?? 0) - (currentPartNoData.recentActual?.productionPerEquipment ?? 0)).toFixed(2) + " pcs"}
                          </Td>
                        </Tr>

                        {/* 1人あたり生産性（計算値） */}
                        <Tr bg="gray.50" h="48px">
                          <Td borderRight="1px solid" borderColor="gray.200">
                            <HStack spacing={1}>
                              <Text>{t('components.RationalizationSummaryInputPopover.label.productionPerPerson')}</Text>
                              <Popover placement="auto">
                                <PopoverTrigger>
                                  <Button style={{ background: 'none', border: 'none', padding: 0 }} minW="auto" h="auto" ml={1}>
                                    <FaRegQuestionCircle size="16px" cursor="pointer" />
                                  </Button>
                                </PopoverTrigger>
                                <Portal>
                                  <PopoverContent width="fit-content" bgColor="#fffff0">
                                    <PopoverArrow />
                                    <PopoverBody>
                                      <Text fontSize="sm">{t('components.RationalizationSummaryInputPopover.help.productionPerPersonFormula')}</Text>
                                    </PopoverBody>
                                  </PopoverContent>
                                </Portal>
                              </Popover>
                            </HStack>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                            {safeNumber(currentPartNoData.recentActual?.productionPerPerson ?? 0).toFixed(2) + " pcs"}
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                            {safeNumber(currentPartNoData.theoretical?.productionPerPerson ?? 0).toFixed(2) + " pcs"}
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                            {safeNumber(currentPartNoData.investmentPlan?.productionPerPerson ?? 0).toFixed(2) + " pcs"}
                          </Td>
                          <Td textAlign="center">
                            {safeNumber((currentPartNoData.investmentPlan?.productionPerPerson ?? 0) - (currentPartNoData.recentActual?.productionPerPerson ?? 0)).toFixed(2) + " pcs"}
                          </Td>
                        </Tr>
                      </Tbody>
                    </Table>
                  )}
                </fieldset>
              </Box>
            </Box>

            <fieldset disabled={!pagePermissions?.isEditable} style={!pagePermissions?.isEditable ? { pointerEvents: 'none' } : undefined}>
              <VStack align="stretch" spacing={4}>
                {/* 分析 */}
                <Box>
                  <Text fontWeight="bold" mb={3}>{t('components.RationalizationSummaryInputPopover.section.analysis')}</Text>
                  <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={4} fontSize="sm">
                    <VStack align="stretch" spacing={2}>
                      {/* 1行目: 合理化の理由 / 非加工対策 */}
                      <HStack align="stretch" spacing={4}>
                        <FormControl isRequired isInvalid={!!errors.rationalizationReason.isError} flex={1}>
                          <FormLabel fontSize="sm">{t('components.RationalizationSummaryInputPopover.label.rationalizationReason')}</FormLabel>
                          <Textarea
                            size="sm"
                            value={localRationalizationSummary.rationalizationReason || ""}
                            onChange={(e) => handleTextFieldUpdate("rationalizationReason", e.target.value)}
                            rows={3}
                            placeholder={t('components.RationalizationSummaryInputPopover.placeholder.rationalizationReason')}
                          />
                          <Box minH="20px">
                            <FormErrorMessage>{errors.rationalizationReason.errorMessage}</FormErrorMessage>
                          </Box>
                        </FormControl>
                        <FormControl isRequired isInvalid={!!errors.nonProcessingMeasures.isError} flex={1}>
                          <FormLabel fontSize="sm">{t('components.RationalizationSummaryInputPopover.label.nonProcessingMeasures')}</FormLabel>
                          <Textarea
                            size="sm"
                            value={localRationalizationSummary.nonProcessingMeasures || ""}
                            onChange={(e) => handleTextFieldUpdate("nonProcessingMeasures", e.target.value)}
                            rows={3}
                            placeholder={t('components.RationalizationSummaryInputPopover.placeholder.nonProcessingMeasures')}
                          />
                          <Box minH="20px">
                            <FormErrorMessage>{errors.nonProcessingMeasures.errorMessage}</FormErrorMessage>
                          </Box>
                        </FormControl>
                      </HStack>
                      {/* 2行目: 非稼働対策 / 1人あたり生産性 */}
                      <HStack align="stretch" spacing={4}>
                        <FormControl isRequired isInvalid={!!errors.nonOperationMeasures.isError} flex={1}>
                          <FormLabel fontSize="sm">{t('components.RationalizationSummaryInputPopover.label.nonOperationMeasures')}</FormLabel>
                          <Textarea
                            size="sm"
                            value={localRationalizationSummary.nonOperationMeasures || ""}
                            onChange={(e) => handleTextFieldUpdate("nonOperationMeasures", e.target.value)}
                            rows={3}
                            placeholder={t('components.RationalizationSummaryInputPopover.placeholder.nonOperationMeasures')}
                          />
                          <Box minH="20px">
                            <FormErrorMessage>{errors.nonOperationMeasures.errorMessage}</FormErrorMessage>
                          </Box>
                        </FormControl>
                        <FormControl isRequired isInvalid={!!errors.productionPerPerson.isError} flex={1}>
                          <FormLabel fontSize="sm">{t('components.RationalizationSummaryInputPopover.label.productionPerPersonAnalysis')}</FormLabel>
                          <Textarea
                            size="sm"
                            value={localRationalizationSummary.productionPerPerson || ""}
                            onChange={(e) => handleTextFieldUpdate("productionPerPerson", e.target.value)}
                            rows={3}
                            placeholder={t('components.RationalizationSummaryInputPopover.placeholder.productionPerPerson')}
                          />
                          <Box minH="20px">
                            <FormErrorMessage>{errors.productionPerPerson.errorMessage}</FormErrorMessage>
                          </Box>
                        </FormControl>
                      </HStack>
                      {/* 3行目: 設備あたり生産性 / 現行設備の処置方法 */}
                      <HStack align="stretch" spacing={4}>
                        <FormControl isRequired isInvalid={!!errors.productionPerEquipment.isError} flex={1}>
                          <FormLabel fontSize="sm">{t('components.RationalizationSummaryInputPopover.label.productionPerEquipmentAnalysis')}</FormLabel>
                          <Textarea
                            size="sm"
                            value={localRationalizationSummary.productionPerEquipment || ""}
                            onChange={(e) => handleTextFieldUpdate("productionPerEquipment", e.target.value)}
                            rows={3}
                            placeholder={t('components.RationalizationSummaryInputPopover.placeholder.productionPerEquipment')}
                          />
                          <Box minH="20px">
                            <FormErrorMessage>{errors.productionPerEquipment.errorMessage}</FormErrorMessage>
                          </Box>
                        </FormControl>
                        <FormControl isRequired isInvalid={!!errors.currentEquipmentHandling.isError} flex={1}>
                          <FormLabel fontSize="sm">{t('components.RationalizationSummaryInputPopover.label.currentEquipmentHandling')}</FormLabel>
                          <Textarea
                            size="sm"
                            value={localRationalizationSummary.currentEquipmentHandling || ""}
                            onChange={(e) => handleTextFieldUpdate("currentEquipmentHandling", e.target.value)}
                            rows={3}
                            placeholder={t('components.RationalizationSummaryInputPopover.placeholder.currentEquipmentHandling')}
                          />
                          <Box minH="20px">
                            <FormErrorMessage>{errors.currentEquipmentHandling.errorMessage}</FormErrorMessage>
                          </Box>
                        </FormControl>
                      </HStack>
                      {/* 4行目: 省人化の計画 / 合理化による期待効果 */}
                      <HStack align="stretch" spacing={4}>
                        <FormControl isRequired isInvalid={!!errors.laborSavingPlan.isError} flex={1}>
                          <FormLabel fontSize="sm">{t('components.RationalizationSummaryInputPopover.label.laborSavingPlan')}</FormLabel>
                          <Textarea
                            size="sm"
                            value={localRationalizationSummary.laborSavingPlan || ""}
                            onChange={(e) => handleTextFieldUpdate("laborSavingPlan", e.target.value)}
                            rows={3}
                            placeholder={t('components.RationalizationSummaryInputPopover.placeholder.laborSavingPlan')}
                          />
                          <Box minH="20px">
                            <FormErrorMessage>{errors.laborSavingPlan.errorMessage}</FormErrorMessage>
                          </Box>
                        </FormControl>
                        <FormControl isRequired isInvalid={!!errors.expectedEffect.isError} flex={1}>
                          <FormLabel fontSize="sm">{t('components.RationalizationSummaryInputPopover.label.expectedEffect')}</FormLabel>
                          <Textarea
                            size="sm"
                            value={localRationalizationSummary.expectedEffect || ""}
                            onChange={(e) => handleTextFieldUpdate("expectedEffect", e.target.value)}
                            rows={3}
                            placeholder={t('components.RationalizationSummaryInputPopover.placeholder.expectedEffect')}
                          />
                          <Box minH="20px">
                            <FormErrorMessage>{errors.expectedEffect.errorMessage}</FormErrorMessage>
                          </Box>
                        </FormControl>
                      </HStack>
                    </VStack>
                  </Box>
                </Box>

                {/* 指標グループ */}
                <Box>
                  <Text fontWeight="bold" mb={3}>{t('components.RationalizationSummaryInputPopover.section.indicator')}</Text>
                  <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={4} fontSize="sm">
                    {/* 指標項目追加エリア */}
                    <HStack spacing={4} mb={4} alignItems="center">
                      <Text fontSize="sm" fontWeight="medium">{t('components.RationalizationSummaryInputPopover.label.item')}</Text>
                      <Select
                        size="sm"
                        w="200px"
                        value={selectedIndicatorItemType}
                        onChange={(e) => handleItemTypeChange("indicator", e.target.value as RationalizationProfitLossItemType | "")}
                      >
                        {INDICATOR_ITEM_OPTIONS.filter((option) =>
                          option.value === "" || option.value === "custom" || !indicatorItems.some(item => item.itemType === option.value)
                        ).map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </Select>

                      <ReadOnlyExemptButton colorScheme="blue" onClick={() => handleGroupGoogleSpread("indicator")} isLoading={isLoading} isDisabled={indicatorItems.length === 0}>GoogleSpread</ReadOnlyExemptButton>
                      <Button colorScheme="blue" onClick={() => handleGroupDataLink("indicator")} isLoading={isLoading} isDisabled={!activeIndicatorSpreadsheetId || isIndicatorSpreadsheetWindowClosed}>{t('components.RationalizationSummaryInputPopover.button.dataLink')}</Button>
                      <Popover placement="auto">
                        <PopoverTrigger>
                          <Button style={{ background: 'none', border: 'none', padding: 0 }} minW="auto" h="auto">
                            <FaRegQuestionCircle size="16px" cursor="pointer" />
                          </Button>
                        </PopoverTrigger>
                        <Portal>
                          <PopoverContent width="400px" bgColor="#fffff0">
                            <PopoverArrow />
                            <PopoverBody>
                              <VStack align="start" spacing={1} fontSize="sm">
                                <UnorderedList>
                                  <ListItem>
                                    <Button colorScheme="blue" size="xs">{t('components.RationalizationSummaryInputPopover.button.dataLink')}</Button>{t('components.RationalizationSummaryInputPopover.help.dataLinkExcludeDescription')}
                                    <UnorderedList>
                                      <ListItem>{t('components.RationalizationSummaryInputPopover.help.dataLinkExcludeNoItem')}</ListItem>
                                      <ListItem>{t('components.RationalizationSummaryInputPopover.help.dataLinkExcludeInvalidKbn')}</ListItem>
                                      <ListItem>{t('components.RationalizationSummaryInputPopover.help.dataLinkExcludeInvalidMonth')}</ListItem>
                                    </UnorderedList>
                                  </ListItem>
                                  <ListItem>{t('components.RationalizationSummaryInputPopover.help.dataLinkItemErrorIndicator')}</ListItem>
                                  <ListItem>{t('components.RationalizationSummaryInputPopover.help.dataLinkMonthError')}<Button colorScheme="blue" size="xs">{t('components.RationalizationSummaryInputPopover.button.dataLink')}</Button>{t('components.RationalizationSummaryInputPopover.help.dataLinkMonthErrorSuffix')}</ListItem>
                                </UnorderedList>
                              </VStack>
                            </PopoverBody>
                          </PopoverContent>
                        </Portal>
                      </Popover>
                      <Text fontSize="xs" color="gray.600">{t('components.RationalizationSummaryInputPopover.help.compareNote')}</Text>
                    </HStack>

                    {/* 年月がない場合の警告 */}
                    {monthList.length === 0 && (
                      <Text color="orange.500" mb={4}>
                        {t('components.RationalizationSummaryInputPopover.message.noMonthWarning', { startDateFieldName: t('features.InvestmentScreening.BasicInfo.field.projectStartDate'), endDateFieldName: t('features.InvestmentScreening.BasicInfo.field.projectEndDate') })}
                      </Text>
                    )}

                    {/* 指標テーブル */}
                    {monthList.length > 0 && (
                      <Box position="relative" h="370px" border="1px solid #ccc">
                        <Box position="absolute" top={0} left={0} right={0} bottom={0} overflowX="auto" overflowY="auto">
                          <Table variant="simple" size="sm" style={{ tableLayout: "fixed", borderCollapse: "separate" }} cellSpacing={0} className=" mm-ceas-table-extend">
                            <colgroup>
                              <col width={`${WIDTH_ITEM_NAME}px`} />
                              <col width={`${WIDTH_OPERATE}px`} />
                              <col width={`${WIDTH_KBN}px`} />
                              {monthList.map((month) => (
                                <col key={month} width={`${WIDTH_VALUE}px`} />
                              ))}
                            </colgroup>
                            <Thead position="sticky" top={0} bg="white" zIndex="10">
                              <Tr>
                                <Th position="sticky" left={`${getFixedColumnLeft(1)}px`} zIndex="10">{t('components.RationalizationSummaryInputPopover.label.item')}</Th>
                                <Th position="sticky" left={`${getFixedColumnLeft(2)}px`} zIndex="10">{t('components.RationalizationSummaryInputPopover.label.operate')}</Th>
                                <Th position="sticky" left={`${getFixedColumnLeft(3)}px`} zIndex="10">{t('components.RationalizationSummaryInputPopover.label.kbn')}</Th>
                                {monthList.map((month) => (
                                  <Th key={month}>
                                    {formatMonthDisplay(month)}
                                  </Th>
                                ))}
                              </Tr>
                            </Thead>
                            <Tbody>
                              {indicatorItems.map((item, indicatorIndex) => (
                                <React.Fragment key={item.id}>
                                  {/* 合理化前の行 */}
                                  <Tr h="40px"
                                    onMouseEnter={() => setHoveredIndicatorItemId(item.id)}
                                    onMouseLeave={() => setHoveredIndicatorItemId(null)}
                                  >
                                    <Td
                                      position="sticky"
                                      left={`${getFixedColumnLeft(1)}px`}
                                      zIndex="1"
                                      rowSpan={2}
                                      verticalAlign="middle"
                                      boxShadow={indicatorErrorCells.has(`${item.id}:item`) ? "inset 0 0 0 2px #E53E3E" : undefined}
                                      style={{ background: hoveredIndicatorItemId === item.id ? "#eee" : "#fff" }}
                                    >
                                      <HStack spacing={1} align="center">
                                        {item.itemType === "custom" ? (
                                          <Text
                                            as="span"
                                            cursor={!pagePermissions?.isEditable ? "default" : "pointer"}
                                            color={!pagePermissions?.isEditable ? "black" : "blue.600"}
                                            textDecoration={!pagePermissions?.isEditable ? "none" : "underline"}
                                            _hover={{ color: "blue.800" }}
                                            onClick={() => {
                                              if (!pagePermissions?.isEditable) return;
                                              handleEditCustomItem("indicator", item);
                                            }}
                                          >
                                            {getItemDisplayName(item)}
                                          </Text>
                                        ) : (
                                          <Text as="span">{getItemDisplayName(item)}</Text>
                                        )}
                                        {requiredIndicatorItemTypes.includes(item.itemType) && (
                                          <Badge colorScheme="red" variant="subtle">
                                            {t('components.RationalizationSummaryInputPopover.badge.required')}
                                          </Badge>
                                        )}
                                      </HStack>
                                    </Td>
                                    <Td position="sticky" left={`${getFixedColumnLeft(2)}px`} zIndex="1" rowSpan={2} verticalAlign="middle"
                                      style={{ background: hoveredIndicatorItemId === item.id ? "#eee" : "#fff" }}
                                    >
                                      <VStack spacing={0}>
                                        <Button size="xs" variant="ghost" onClick={() => moveItem("indicator", item.id, "up")}
                                          isDisabled={indicatorIndex === 0} title={t('common.button.moveUp')}>
                                          <FiChevronUp />
                                        </Button>
                                        <Button size="xs" variant="ghost" onClick={() => moveItem("indicator", item.id, "down")}
                                          isDisabled={indicatorIndex === indicatorItems.length - 1} title={t('common.button.moveDown')}>
                                          <FiChevronDown />
                                        </Button>
                                        {!requiredIndicatorItemTypes.includes(item.itemType) && (
                                          <Button size="xs" colorScheme="red" variant="ghost" onClick={() => removeItem("indicator", item.id)} title={t('common.button.delete')}>
                                            <FiTrash2 />
                                          </Button>
                                        )}
                                      </VStack>
                                    </Td>
                                    <Td position="sticky" left={`${getFixedColumnLeft(3)}px`} zIndex="1"
                                      style={{ background: hoveredIndicatorItemId === item.id ? "#eee" : "#fff" }}
                                    >
                                      {t('components.RationalizationSummaryInputPopover.label.beforeRationalization')}
                                    </Td>
                                    {monthList.map((month) => (
                                      <Td
                                        key={month}
                                        textAlign="right"
                                        boxShadow={indicatorErrorCells.has(`${item.id}:${month}:before`) ? "inset 0 0 0 2px #E53E3E" : undefined}
                                        style={{ background: hoveredIndicatorItemId === item.id ? "#eee" : "#fff" }}
                                      >
                                        {formatNumber(item.beforeMonthlyData[month] ?? 0, "", 0)} {getItemUnit(item, "indicator")}
                                      </Td>
                                    ))}
                                  </Tr>
                                  {/* 合理化後の行 */}
                                  <Tr h="40px"
                                    onMouseEnter={() => setHoveredIndicatorItemId(item.id)}
                                    onMouseLeave={() => setHoveredIndicatorItemId(null)}
                                  >
                                    <Td position="sticky" left={`${getFixedColumnLeft(3)}px`} zIndex="1"
                                      style={{ background: hoveredIndicatorItemId === item.id ? "#eee" : "#fff" }}
                                    >
                                      {t('components.RationalizationSummaryInputPopover.label.afterRationalization')}
                                    </Td>
                                    {monthList.map((month) => (
                                      <Td
                                        key={month}
                                        boxShadow={indicatorErrorCells.has(`${item.id}:${month}:after`) ? "inset 0 0 0 2px #E53E3E" : undefined}
                                        textAlign="right"
                                        style={{ background: hoveredIndicatorItemId === item.id ? "#eee" : "#fff" }}
                                      >
                                        {formatNumber(item.afterMonthlyData[month] ?? 0, "", 0)} {getItemUnit(item, "indicator")}
                                      </Td>
                                    ))}
                                  </Tr>
                                </React.Fragment>
                              ))}
                              {indicatorItems.length === 0 && (
                                <Tr>
                                  <Td colSpan={3 + monthList.length} textAlign="center" color="gray.500">
                                    {t('components.RationalizationSummaryInputPopover.message.addIndicatorItem')}
                                  </Td>
                                </Tr>
                              )}
                            </Tbody>
                          </Table>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* 費用グループ */}
                <Box>
                  <Text fontWeight="bold" mb={3}>{t('components.RationalizationSummaryInputPopover.section.cost')}</Text>
                  <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={4} fontSize="sm">
                    {/* 費用項目追加エリア */}
                    <HStack spacing={4} mb={4} alignItems="center">
                      <Text fontSize="sm" fontWeight="medium">{t('components.RationalizationSummaryInputPopover.label.item')}</Text>
                      <Select
                        size="sm"
                        w="200px"
                        value={selectedCostItemType}
                        onChange={(e) => handleItemTypeChange("cost", e.target.value as RationalizationProfitLossItemType | "")}
                      >
                        {COST_ITEM_OPTIONS.filter((option) =>
                          option.value === "" || option.value === "custom" || !costItems.some(item => item.itemType === option.value)
                        ).map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </Select>

                      <ReadOnlyExemptButton colorScheme="blue" onClick={() => handleGroupGoogleSpread("cost")} isLoading={isLoading} isDisabled={costItems.length === 0}>GoogleSpread</ReadOnlyExemptButton>
                      <Button colorScheme="blue" onClick={() => handleGroupDataLink("cost")} isLoading={isLoading} isDisabled={!activeCostSpreadsheetId || isCostSpreadsheetWindowClosed}>{t('components.RationalizationSummaryInputPopover.button.dataLink')}</Button>
                      <Popover placement="auto">
                        <PopoverTrigger>
                          <Button style={{ background: 'none', border: 'none', padding: 0 }} minW="auto" h="auto">
                            <FaRegQuestionCircle size="16px" cursor="pointer" />
                          </Button>
                        </PopoverTrigger>
                        <Portal>
                          <PopoverContent width="400px" bgColor="#fffff0">
                            <PopoverArrow />
                            <PopoverBody>
                              <VStack align="start" spacing={1} fontSize="sm">
                                <UnorderedList>
                                  <ListItem>
                                    <Button colorScheme="blue" size="xs">{t('components.RationalizationSummaryInputPopover.button.dataLink')}</Button>{t('components.RationalizationSummaryInputPopover.help.dataLinkExcludeDescription')}
                                    <UnorderedList>
                                      <ListItem>{t('components.RationalizationSummaryInputPopover.help.dataLinkExcludeNoItem')}</ListItem>
                                      <ListItem>{t('components.RationalizationSummaryInputPopover.help.dataLinkExcludeInvalidKbn')}</ListItem>
                                      <ListItem>{t('components.RationalizationSummaryInputPopover.help.dataLinkExcludeInvalidMonth')}</ListItem>
                                    </UnorderedList>
                                  </ListItem>
                                  <ListItem>{t('components.RationalizationSummaryInputPopover.help.dataLinkItemErrorCost')}</ListItem>
                                  <ListItem>{t('components.RationalizationSummaryInputPopover.help.dataLinkMonthError')}<Button colorScheme="blue" size="xs">{t('components.RationalizationSummaryInputPopover.button.dataLink')}</Button>{t('components.RationalizationSummaryInputPopover.help.dataLinkMonthErrorSuffix')}</ListItem>
                                </UnorderedList>
                              </VStack>
                            </PopoverBody>
                          </PopoverContent>
                        </Portal>
                      </Popover>
                      <Text fontSize="sm" fontWeight="medium" ml={4}>{t('components.RationalizationSummaryInputPopover.label.currency')}</Text>
                      <Box>
                        <FormControl w="120px" isInvalid={!!errors.costCurrency.isError}>
                          <Select
                            size="sm"
                            value={selectedCostCurrency}
                            onChange={(e) => {
                              const newCurrencyId = e.target.value;
                              const targetItem = costItems.find(item => item.itemType === "targetEquipmentDepreciation");
                              const updateBefore = !targetItem?.isManuallyEditedBefore;
                              const updateAfter = !targetItem?.isManuallyEditedAfter;
                              if ((updateBefore || updateAfter) && equipmentCacheRef.current) {
                                // キャッシュあり：同期計算 → setSelectedCostCurrency と同一イベントでバッチ処理
                                calcDepreciationFromCache(newCurrencyId, equipmentCacheRef.current, { updateBefore, updateAfter });
                              } else if (updateBefore || updateAfter) {
                                // キャッシュなし（まれなケース）：非同期フォールバック
                                calcTargetEquipmentDepreciation({ updateBefore, updateAfter, targetCurrency: newCurrencyId }).catch(err => console.error("償却費自動計算エラー:", err));
                              }
                              setSelectedCostCurrency(newCurrencyId);
                            }}
                          >
                            <option value="">{t('components.RationalizationSummaryInputPopover.option.selectPlaceholder')}</option>
                            {currencyData.map((currency) => (
                              <option key={currency.currencyId} value={currency.currencyId}>{currency.currencyName}</option>
                            ))}
                          </Select>
                          <FormErrorMessage fontSize="xs">{errors.costCurrency.errorMessage}</FormErrorMessage>
                        </FormControl>
                      </Box>
                      <Text fontSize="xs" color="gray.600">{t('components.RationalizationSummaryInputPopover.help.compareNote')}</Text>
                    </HStack>

                    {/* 年月がない場合の警告 */}
                    {monthList.length === 0 && (
                      <Text color="orange.500" mb={4}>
                        {t('components.RationalizationSummaryInputPopover.message.noMonthWarning', { startDateFieldName: t('features.InvestmentScreening.BasicInfo.field.projectStartDate'), endDateFieldName: t('features.InvestmentScreening.BasicInfo.field.projectEndDate') })}
                      </Text>
                    )}

                    {/* 費用テーブル */}
                    {monthList.length > 0 && (
                      <Box position="relative" h="370px" border="1px solid #ccc">
                        <Box position="absolute" top={0} left={0} right={0} bottom={0} overflowX="auto" overflowY="auto">
                          <Table variant="simple" size="sm" style={{ tableLayout: "fixed", borderCollapse: "separate" }} cellSpacing={0} className=" mm-ceas-table-extend">
                            <colgroup>
                              <col width={`${WIDTH_ITEM_NAME}px`} />
                              <col width={`${WIDTH_OPERATE}px`} />
                              <col width={`${WIDTH_KBN}px`} />
                              {monthList.map((month) => (
                                <col key={month} width={`${WIDTH_VALUE}px`} />
                              ))}
                            </colgroup>
                            <Thead position="sticky" top={0} bg="white" zIndex="10">
                              <Tr>
                                <Th position="sticky" left={`${getFixedColumnLeft(1)}px`} zIndex="10">{t('components.RationalizationSummaryInputPopover.label.item')}</Th>
                                <Th position="sticky" left={`${getFixedColumnLeft(2)}px`} zIndex="10">{t('components.RationalizationSummaryInputPopover.label.operate')}</Th>
                                <Th position="sticky" left={`${getFixedColumnLeft(3)}px`} zIndex="10">{t('components.RationalizationSummaryInputPopover.label.kbn')}</Th>
                                {monthList.map((month) => (
                                  <Th key={month}>
                                    {formatMonthDisplay(month)}
                                  </Th>
                                ))}
                              </Tr>
                            </Thead>
                            <Tbody>
                              {costItems.map((item, costIndex) => (
                                <React.Fragment key={item.id}>
                                  {/* 合理化前の行 */}
                                  <Tr h="40px"
                                    onMouseEnter={() => setHoveredCostItemId(item.id)}
                                    onMouseLeave={() => setHoveredCostItemId(null)}
                                  >
                                    <Td position="sticky" left={`${getFixedColumnLeft(1)}px`} zIndex="1" rowSpan={2} verticalAlign="middle"
                                      style={{ background: hoveredCostItemId === item.id ? "#eee" : "#fff" }}
                                    >
                                      <HStack spacing={1} align="center">
                                        {item.itemType === "custom" ? (
                                          <Text
                                            as="span"
                                            cursor={!pagePermissions?.isEditable ? "default" : "pointer"}
                                            color={!pagePermissions?.isEditable ? "black" : "blue.600"}
                                            textDecoration={!pagePermissions?.isEditable ? "none" : "underline"}
                                            _hover={{ color: "blue.800" }}
                                            onClick={() => {
                                              if (!pagePermissions?.isEditable) return;
                                              handleEditCustomItem("cost", item);
                                            }}
                                          >
                                            {getItemDisplayName(item)}
                                          </Text>
                                        ) : (
                                          <Text as="span">{getItemDisplayName(item)}</Text>
                                        )}
                                        {requiredCostItemTypes.includes(item.itemType) && (
                                          <Badge colorScheme="red" variant="subtle">
                                            {t('components.RationalizationSummaryInputPopover.badge.required')}
                                          </Badge>
                                        )}
                                      </HStack>
                                      {item.itemType === "targetEquipmentDepreciation" && (
                                        <>
                                          <VStack mt={1} spacing="2px" align="flex-start">
                                            <HStack spacing={1}>
                                              <Badge
                                                colorScheme={item.isManuallyEditedBefore ? "blue" : "gray"}
                                                fontSize="10px"
                                              >
                                                {item.isManuallyEditedBefore
                                                  ? t('components.RationalizationSummaryInputPopover.badge.manuallyEditedBefore')
                                                  : t('components.RationalizationSummaryInputPopover.badge.autoCalculatedBefore')}
                                              </Badge>
                                              {pagePermissions?.isEditable && item.isManuallyEditedBefore && (
                                                <Button
                                                  size="xs"
                                                  variant="link"
                                                  colorScheme="blue"
                                                  fontSize="10px"
                                                  onClick={() => calcTargetEquipmentDepreciation({ updateBefore: true, updateAfter: false })}
                                                >
                                                  {t('components.RationalizationSummaryInputPopover.badge.resetToAutoCalc')}
                                                </Button>
                                              )}
                                            </HStack>
                                            <HStack spacing={1}>
                                              <Badge
                                                colorScheme={item.isManuallyEditedAfter ? "blue" : "gray"}
                                                fontSize="10px"
                                              >
                                                {item.isManuallyEditedAfter
                                                  ? t('components.RationalizationSummaryInputPopover.badge.manuallyEditedAfter')
                                                  : t('components.RationalizationSummaryInputPopover.badge.autoCalculatedAfter')}
                                              </Badge>
                                              {pagePermissions?.isEditable && item.isManuallyEditedAfter && (
                                                <Button
                                                  size="xs"
                                                  variant="link"
                                                  colorScheme="blue"
                                                  fontSize="10px"
                                                  onClick={() => calcTargetEquipmentDepreciation({ updateBefore: false, updateAfter: true })}
                                                >
                                                  {t('components.RationalizationSummaryInputPopover.badge.resetToAutoCalc')}
                                                </Button>
                                              )}
                                            </HStack>
                                          </VStack>
                                        </>
                                      )}
                                    </Td>
                                    <Td position="sticky" left={`${getFixedColumnLeft(2)}px`} zIndex="1" rowSpan={2} verticalAlign="middle"
                                      style={{ background: hoveredCostItemId === item.id ? "#eee" : "#fff" }}
                                    >
                                      <VStack spacing={0}>
                                        <Button size="xs" variant="ghost" onClick={() => moveItem("cost", item.id, "up")}
                                          isDisabled={costIndex === 0} title={t('common.button.moveUp')}>
                                          <FiChevronUp />
                                        </Button>
                                        <Button size="xs" variant="ghost" onClick={() => moveItem("cost", item.id, "down")}
                                          isDisabled={costIndex === costItems.length - 1} title={t('common.button.moveDown')}>
                                          <FiChevronDown />
                                        </Button>
                                        {!requiredCostItemTypes.includes(item.itemType) && (
                                          <Button size="xs" colorScheme="red" variant="ghost" onClick={() => removeItem("cost", item.id)} title={t('common.button.delete')}>
                                            <FiTrash2 />
                                          </Button>
                                        )}
                                      </VStack>
                                    </Td>
                                    <Td position="sticky" left={`${getFixedColumnLeft(3)}px`} zIndex="1"
                                      style={{ background: hoveredCostItemId === item.id ? "#eee" : "#fff" }}
                                    >
                                      {t('components.RationalizationSummaryInputPopover.label.beforeRationalization')}
                                    </Td>
                                    {monthList.map((month) => (
                                      <Td
                                        key={month}
                                        textAlign="right"
                                        boxShadow={costErrorCells.has(`${item.id}:${month}:before`) ? "inset 0 0 0 2px #E53E3E" : undefined}
                                        style={{ background: hoveredCostItemId === item.id ? "#eee" : "#fff" }}
                                      >
                                        {formatNumber(item.beforeMonthlyData[month] ?? 0, getItemUnit(item, "cost"))} {getItemUnit(item, "cost")}
                                      </Td>
                                    ))}
                                  </Tr>
                                  {/* 合理化後の行 */}
                                  <Tr h="40px"
                                    onMouseEnter={() => setHoveredCostItemId(item.id)}
                                    onMouseLeave={() => setHoveredCostItemId(null)}
                                  >
                                    <Td position="sticky" left={`${getFixedColumnLeft(3)}px`} zIndex="1"
                                      style={{ background: hoveredCostItemId === item.id ? "#eee" : "#fff" }}
                                    >
                                      {t('components.RationalizationSummaryInputPopover.label.afterRationalization')}
                                    </Td>
                                    {monthList.map((month) => (
                                      <Td
                                        key={month}
                                        textAlign="right"
                                        boxShadow={costErrorCells.has(`${item.id}:${month}:after`) ? "inset 0 0 0 2px #E53E3E" : undefined}
                                        style={{ background: hoveredCostItemId === item.id ? "#eee" : "#fff" }}
                                      >
                                        {formatNumber(item.afterMonthlyData[month] ?? 0, getItemUnit(item, "cost"))} {getItemUnit(item, "cost")}
                                      </Td>
                                    ))}
                                  </Tr>
                                </React.Fragment>
                              ))}
                              {costItems.length === 0 && (
                                <Tr>
                                  <Td colSpan={3 + monthList.length} textAlign="center" color="gray.500">
                                    {t('components.RationalizationSummaryInputPopover.message.addCostItem')}
                                  </Td>
                                </Tr>
                              )}
                            </Tbody>
                          </Table>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* 自由入力モーダル */}
                <Modal isOpen={isCustomItemModalOpen} onClose={handleCustomItemCancel} size="md">
                  <ModalOverlay />
                  <ModalContent>
                    <ModalHeader>{t('components.RationalizationSummaryInputPopover.modal.customItemTitle')}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                      <VStack spacing={4} align="stretch">
                        <FormControl isRequired>
                          <FormLabel fontSize="sm">{t('components.RationalizationSummaryInputPopover.label.itemName')}</FormLabel>
                          <Input
                            size="sm"
                            value={customItemName}
                            onChange={(e) => setCustomItemName(e.target.value)}
                            placeholder={t('components.RationalizationSummaryInputPopover.placeholder.itemName')}
                          />
                        </FormControl>
                        <FormControl isRequired>
                          <HStack spacing={1} alignItems="center">
                            <FormLabel fontSize="sm" mb={0}>{t('components.RationalizationSummaryInputPopover.label.unit')}</FormLabel>
                            {customItemTargetGroup === "indicator" && (
                              <Popover placement="auto">
                                <PopoverTrigger>
                                  <Button style={{ background: 'none', border: 'none', padding: 0 }} minW="auto" h="auto">
                                    <FaRegQuestionCircle size="14px" cursor="pointer" />
                                  </Button>
                                </PopoverTrigger>
                                <Portal>
                                  <PopoverContent width="fit-content" bgColor="#fffff0">
                                    <PopoverArrow />
                                    <PopoverBody>
                                      <Text fontSize="sm">{t('components.RationalizationSummaryInputPopover.help.unitFreeInput')}</Text>
                                    </PopoverBody>
                                  </PopoverContent>
                                </Portal>
                              </Popover>
                            )}
                          </HStack>
                          <Input
                            size="sm"
                            value={customItemTargetGroup === "cost" ? t('components.RationalizationSummaryInputPopover.label.selectedCurrency') : customUnit}
                            onChange={(e) => setCustomUnit(e.target.value)}
                            placeholder={t('components.RationalizationSummaryInputPopover.placeholder.unitInput')}
                            isDisabled={customItemTargetGroup === "cost"}
                          />
                        </FormControl>
                      </VStack>
                    </ModalBody>
                    <ModalFooter>
                      <Button
                        colorScheme="blue"
                        size="sm"
                        mr={2}
                        isDisabled={!customItemName.trim() || !customUnit.trim()}
                        onClick={handleCustomItemConfirm}
                      >
                        {t('components.RationalizationSummaryInputPopover.button.confirm')}
                      </Button>
                      <Button size="sm" onClick={handleCustomItemCancel}>
                        {t('components.RationalizationSummaryInputPopover.button.cancel')}
                      </Button>
                    </ModalFooter>
                  </ModalContent>
                </Modal>

              </VStack>
            </fieldset>
          </ModalBody>
          <ModalFooter>
            {pagePermissions?.isEditable && (
              <>
                <Button colorScheme="teal" variant="outline" mr={2} onClick={handleTempSave}>{t('components.RationalizationSummaryInputPopover.button.tempSave')}</Button>
                <Button colorScheme="teal" variant="outline" mr={2} onClick={handleTempLoad}>{t('components.RationalizationSummaryInputPopover.button.tempLoad')}</Button>
              </>
            )}
            <Button colorScheme="blue"
              isLoading={isLoading}
              isDisabled={isSaving || !pagePermissions?.isEditable}
              onClick={() => {
                if (validate() === false) {
                  toast({
                    title: TITLE,
                    description: t('components.RationalizationSummaryInputPopover.message.inputError'),
                    status: 'warning',
                    duration: 2000,
                    isClosable: true,
                  });
                  return;
                }
                handleSave();
              }}>
              {t('components.RationalizationSummaryInputPopover.button.save')}
            </Button>
            <Button isLoading={isLoading} onClick={handleReset} ml={2}>
              {t('components.RationalizationSummaryInputPopover.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default RationalizationSummaryInputPopover;
